/**
 * @ngdoc Controller
 * @name InstallationDashboardController
 */
import angular from 'angular';
import { sprintf } from '@/angular/libs/sprintf';
import { chunk, groupBy, sortBy, sumBy, get } from "lodash";
import HistoryInfoModal from '@/vue/components/modals/HistoryInfoModal';
import { REALTIME_INDICATOR } from '../../mqtt/mqtt.service';
import { parseBoolean } from '@/utils/parse';
import {getTankTypeTranslationKey, getTemperatureSensorTypeTranslationKey} from "@/utils/devices";
import {getSafeInstancesProp} from "@/vue/helpers/handleInstallationsValues";
import { UNITS2FORMATTING, INVALID_VALUE, isValueOutdated } from "@/utils/attributes";
import { generatorRunTimeFormatter } from '@/angular/utils/dashboardHelpers';

angular.module('vrm.core')
    .controller('InstallationDashboardController', InstallationDashboardController);

// utility functions
const getValueFromAttr = (attribute, key) => {
    if (!attribute) {
        return null;
    }
    const { instances } = attribute;
    if (
        instances &&
        instances[key] &&
        instances[key].timestamp &&
        instances[key].rawValue &&
        typeof instances[key].rawValue !== 'string' &&
        isValueOutdated(instances[key].timestamp)
    ) {
        return INVALID_VALUE;
    }
    return instances && instances[key] && (instances[key].formattedValue || instances[key].textValue || instances[key].rawValue);
}

const hasInstances = (scope, attribute) => {
    return scope.ci[attribute] && scope.ci[attribute].instances && Object.values(scope.ci[attribute].instances).length > 0;
}

const includesOneOf = (a, b) => a.some(x => b.includes(x))

const MPPT_REFRESH_ATTRIBUTES = [
    'PVP', 'PVP0', 'PVP1', 'PVP2', 'PVP3', 'PVV0',
    'mPVP', 'mPVP0', 'mPVP1', 'mPVP2', 'mPVP3',
    'iPVP', 'iPVP0', 'iPVP1', 'iPVP2', 'iPVP3',
    'Sccn'
];
const ACTIVE_SOURCE_REFRESH_ATTRIBUTES = ['AIS'];
const BATTERY_REFRESH_ATTRIBUTES = ['abs', 'BT', 'CT', 'ScT', 'bT', 'bs', 'Ba'];
const IS_ESS_TYPE_SYSTEM_REFRESH_ATTRIBUTES = ['st', 'dea', 'aM'];
const VENUS_GUI_REFRESH_ATTRIBUTES = ['ss', 'st', 'si1', 'si2', 'shao', 'rwgm', 'AIS', 'Pj', 'Pi', 'P', 'Pdc', 'shd', 'solar_yield', 'generator', 'from_to_grid'];
const ESS_STATE_ACTIVE_REFRESH_ATTRIBUTES = ['ssls', 'ssbl', 'ssdd', 'ssdc', 'sssc', 'ssuc', 'ssud'];
const AC_SOURCES_REFRESH_ATTRIBUTES = ['generator', 'from_to_grid', 'ini', 'ic0', 'id0', 'vi0', 'ist0', 'is0', 'ic1',
    'id1', 'vi1', 'ist1', 'is1', 'gRC', 'gLS', 'gRT', 'ci.Gt'];
const FLOWS_REFRESH_ATTRIBUTES = ['bp', 'CI', 'CV', 'P', 'Pdc', 'hub_inverter', 'ac_out', 'consumption_output', 'input_power',
    'generator', 'from_to_grid', 'ac_in', 'ac_loads', 'dc', 'bt', 'evp', 'ev_car_charger_out', 'ev_car_charger_in'];
const HAS_SUPPOSED_CONTROLS_REFRESH_ATTRIBUTES = ['st', 'eve', 'H4bs', 'cRelay', 'cRelay1', 'rf0', 'rf1', 's', 'gaA'];
const GENERATOR_PATHS_REFRESH_ATTRIBUTES = ['cp', 'gaA'];
const EV_CHARGER_REFRESH_ATTRIBUTES = ['evp', 'evs', 'evt', 'evm', 'evi', 'evcn', 'evf', 'evl'];
const TEMPERATURE_REFRESH_ATTRIBUTES = ['tsT', 'tscn', 'tst', 'tsH', 'tsB'];
const TANKS_REFRESH_ATTRIBUTES = ['tf', 'tl'];
const CURRENT_LIMIT_REFRESH_ATTRIBUTES = ['AILIM', 'mIL'];
const INPUT_CURRENT_REFRESH_ATTRIBUTES = ['II1', 'II2', 'II3'];
const MULTIPLE_PHASES_REFRESH_ATTRIBUTES = ['PC', 'aps1'];
const LOCATION_REFRESH_ATTRIBUTES = ['lt', 'lg'];

/**
 * @constructor
 * @ngInject
 */
function InstallationDashboardController(
    $injector,
    $location,
    $timeout,
    $interval,
    $filter,
    $state,
    $log,
    $scope,
    $stateParams,
    $q,
    $rootScope,
    GraphService,
    WhitelabelService,
    InstallationsService,
    AppearanceService,
    vrmConfig,
    VrmModalService,
    ApiService,
    locale,
    MQTTRealtimeService,
    localStorageService,
    EssGraphService,
    moment,
    solarPieGraph,
    consumptionPieGraph,
    generatorPieGraph,
    gridPieGraph,
    liveFeedGraph,
    consumptionGraph,
    solarGraph,
    generatorGraph,
    gridGraph,
    evcsGraph,
    costsSavingsGraph,
    essAlgorithmGraph,
    energyPricesGraph,
    usageGraph,
    environmentalGraph,
    dashboardCurrentLimitGraph,
    dashboardBatteryGraph,
    dashboardDcPowerGraph,
    dashboardGeneratorGraph,
    dashboardSolarGraph,
    dashboardAcLoadsGraph,
    dashboardConsumptionGraph,
    dashboardConsumptionOutputGraph,
    dashboardEvPowerGraph,
    dashboardFromToGridGraph,
    vrmNotificationType,
    vrmNotificationsService,
    VrmBufferedMQTTService,
    InstallationAvatarService,
) {
    $log.info('InstallationDashboardController');

    const FLOW_THRESHOLD = 10;
    const DAYS_NEEDED_UNTIL_FORECAST_IS_ACCURATE = 28;
    const SHOW_DETAILS_STORAGEKEY = 'dashboard.details';
    const SHOW_BLOCK_DETAILS_STORAGEKEY = 'installation.*.dashboard.block.details';
    const SHOW_FORECAST_STORAGEKEY = 'dashboard.forecast';
    const SHOW_GRAPH_TAB_STORAGEKEY = 'dashboard.graph_tab';
    const GRID_HISTORY_FORMAT = "%.0F";
    const GRID_HISTORY_FORMAT_ACCURATE = "%.1F";

    // Installation specific storage keys. The "*" is the installation id wildcard.
    const HIDE_DESS_ACCURACY_WARNING_STORAGEKEY_TEMPLATE = 'installation.*.dess_accuracy_warning';
    const HIDE_FORECAST_WARNING_STORAGEKEY_TEMPLATE = 'installation.*.forecast_warning';
    const HIDE_UNRELIABLE_FORECAST_WARNING_STORAGEKEY_TEMPLATE = 'installation.*.unreliable_forecast_warning';

    //Keep reference to shape and GMaps map
    let shape = null;
    let map = null;
    let mapInitialized = false;

    let GraphColors = WhitelabelService.getGraphColors()

    $scope.INVALID_VALUE = INVALID_VALUE;

    //Flags for the Venus GUI
    $scope.acInput = undefined;
    $scope.isParallel = false;
    $scope.hasAcSolar = false;
    $scope.hasAcSolarOnIn = false;
    $scope.hasDcSolar = false;
    $scope.hasDcAndAcSolar = false;
    $scope.loading = true;
    $scope.hasDcPower = false;
    $scope.showVenusGUI = false;
    $scope.showSolarYield = false;
    $scope.batteryTemperature = null;
    $scope.totalSolarYield = null;
    $scope.ci = [];
    $scope.ess = [];
    $scope.groupedTanks = [];
    $scope.temperatures = [];
    $scope.temperaturesMinMaxPerDay = {};
    $scope.showHistoryInfoModal = false;
    $scope.controlDevicesDrawerOpen = false;
    $scope.hasDeviceControls = false;
    $scope.hasSupposedDeviceControls = false;
    $scope.hideDeviceControlsTimer = null;
    $scope.hideDeviceControlsButton = false;
    $scope.location = $location;
    $scope.isAcGenerator = false;
    $scope.isAcGrid = false;
    $scope.hasAcGridKwh = false;
    $scope.hasGeneratorKwhData = false;
    $scope.forecastLoading = false;
    $scope.mppts = [];
    $scope.mpptsRS = [];
    $scope.dynamicEssPromotionEnabled = vrmConfig.dynamicEssPromotionEnabled;
    $scope.dessAccWarningMessage = "";
    $scope.showForecastWarning =  false;
    $scope.showUnreliableForecastWarning = false;
    $scope.showForecastsUnavailable = false;
    $scope.showForcedFullChargeHappenedWarning = false;
    $scope.daysUntilAccurateForecast = 0;
    $scope.last_location_timestamp = 0;
    $scope.blockNextCacheLocationCall = false;
    $scope.dynamicEssIsOn = null;
    $scope.hasDynamicEssConfiguration = null;
    $scope.dailyTotalEarnings = 0;
    $scope.dailyTotalGridEarnings = 0;
    $scope.dailyTotalBatteryCosts = 0;
    $scope.dailyTotalGridCosts = 0;
    $scope.hysteresis = [];
    $scope.isNewFeaturesBannerEnabled = vrmConfig.newFeaturesBannerEnabled;

    const SUPPORTED_DEBUG_PROPS = ["P", "Pdc", "Pi"]
    $scope.debugProps = new Set();
    $scope.buffer = VrmBufferedMQTTService.createBufferFactory($scope);

    $scope.flows = {
        'line-multi-dc': null,
        'line-hub-inverter': null,
        'line-multi-ac-out': null,
        'line-multi-ac-out2': null,
        'line-multi-ac-out3': null,
        'line-multi-ac-in': null,
        'line-ac-pvinv-ac_in': null,
        'line-ac-loads': null,
        'line-hub-ac-in': null,
        'line-hub-generator': null,
        'line-ac-in': null,
        'line-ac-pvinv': null,
        'line-critical-loads': null,
        'line-pv-inverter-ac-out': null,
        'line-hub-ac-loads': null,
        'line-battery-parallel': null,
        'line-battery': null,
        'line-dc_power': null,
        'line-pv-charger': null,
    };

    // Variable to track whether pipe size has changed
    $scope.prevLoweredPvInverterOffset = null;

    function updateFlows() {
        const {
            merge, flow, flowMany,
            isPropertyValid,
            isParallel,
            hasDcPower,
            hasDcAndAcSolar,
            activeSource,
            acInput,
            hasEvCarChargerOut,
            hasEvCarChargerIn
        } = $scope;

        const isValidP = isPropertyValid('P') || isPropertyValid('P2') || isPropertyValid('P3');
        const isValidPdc = isPropertyValid('Pdc');
        const pvChargerAndInverter = (isValidP && isValidPdc);
        const hasCriticalLoad = $scope.isParallel && $scope.hasAcOutSystem;
        const dcPower = hasDcPower || (isParallel && isValidPdc);
        const lineAcInFlow = flow(acInput === 'grid' ? 'from_to_grid' : (acInput === 'genset' ? 'generator' : undefined))


        $scope.flows = {
            'line-multi-dc': merge(flowMany(['bp', 'dc'], ['Pdc']), { 'dcPower': dcPower, 'lowered': isParallel && hasEvCarChargerOut && isValidPdc, 'lowered--pv-inverter': isParallel && hasEvCarChargerOut && isValidP && isValidPdc, }),
            'line-hub-inverter': flow(hasDcAndAcSolar ? 'hub_inverter' : 'ac_out'),
            'line-multi-ac-out': merge(flowMany(['consumption_output', 'ev_car_charger_out'], ['ac_out']), { 'pv-charger-and-inverter': pvChargerAndInverter, 'ev-car-charger': hasEvCarChargerOut }),
            'line-multi-ac-out2': flowMany(['consumption_output'], ['ac_out']),
            'line-multi-ac-out3': flowMany(['consumption_output'], ['ac_out']),
            'line-multi-ac-in': flowMany(['input_power', 'ev_car_charger_in']),
            'line-ac-pvinv-ac_in': merge(flowMany(['ev_car_charger_in', activeSource === 'genset' ? 'generator' : 'from_to_grid', 'ac_in']), { 'ev-charger': hasEvCarChargerIn }),
            'line-ac-loads': flow('consumption_input'),
            'line-hub-ac-in': flow('from_to_grid'),
            'line-hub-ac-in--ev-car': flowMany(['from_to_grid', 'ev_car_charger_in']),
            'line-hub-generator': flow('generator'),
            'line-hub-generator-in--ev-car': flowMany(['generator', 'ev_car_charger_in']),
            'line-ac-in': merge(flowMany(['ev_car_charger_in', acInput === 'grid' ? 'from_to_grid' : (acInput === 'genset' ? 'generator' : undefined)]), { 'ev-charger': hasEvCarChargerIn }),
            'line-ac-in-2': lineAcInFlow,
            'line-ev-car-charger-in': flow('ev_car_charger_in'),
            'line-to-ev-car-charger-out': flow('ev_car_charger_out'),
            'line-ac-pvinv': flow('ac_in'),
            'line-critical-loads': merge(flow('consumption_output'), { 'pv-charger-and-inverter': pvChargerAndInverter && !hasEvCarChargerOut, 'pv-charger-and-ev-charger': hasEvCarChargerOut }),
            'line-pv-inverter-ac-out': merge(flow('ac_out'), { 'pv-charger-and-inverter': pvChargerAndInverter && !hasEvCarChargerOut, 'critical-load': hasCriticalLoad && !hasEvCarChargerOut, 'ev-charger': hasEvCarChargerOut }),
            'line-hub-ac-loads': flow('ac_loads'),
            'line-hub-ac-loads-total': flowMany(['ev_car_charger_out', 'ac_loads']),
            'line-battery-parallel': flow('bp'),
            'line-battery': merge(flowMany(['bp', 'dc']), { 'dcPower': dcPower }),
            'line-dc_power': flow('dc'),
            'line-pv-charger': merge(flow('Pdc'), { 'dcPower': dcPower }),
            'line-from-pv-inverter-ac-out': flow('ac_out'),
            'line-to-critical-loads': flow('consumption_output'),
            'line-hub-ac-loads-5': flow('ev_car_charger_out'),
            'line-ev-car-chargerin-doublehub': flow('ev_car_charger_in')
        };
    }

    /**
     * Update the formattedValue field of the dataAttr object
     * @param dataAttr
     * @param instance if it is given, returns the instance object with the formattedValue updated
     * @returns dataAttr or dataAttr.instances[instance] with formattedValue updated
     */
    function updateFormattedValue(dataAttr, instance = null) {
        if (instance) {
            return {
                ...dataAttr.instances[instance],
                formattedValue: getFormattedValue(dataAttr, null, instance)
            }
        }
        return {
            ...dataAttr,
            formattedValue: getFormattedValue(dataAttr)
        }
    }

    /**
     * Helper function for getting formatted value of an instance
     * @param dataAttr
     * @param instance
     * @returns formattedValue
     */
    function getFormattedValueForInstance(dataAttr, instance) {
        return getFormattedValue(dataAttr, null, instance)
    }

    /**
     * Formats values based on data attribute
     * The function is used for almost all the data values that are shown on dashboard, including strings and enums
     * @param dataAttr
     * @param rawValue
     * @param instance if the value needed to be formatted is of a specific instance of the data attribute, it is passed
     * to the function; This is needed to get the right rawValue
     * @returns formattedValue
     */
    function getFormattedValue(dataAttr, rawValue = null, instance = null) {
        /**
         * Makes unit conversion in both ways using a hysteresis (Ex: 'W' -> 'kW' and 'kW' -> 'W')
         * The threshold for up scaling is 10.000; for down scaling is 9.000; rawValue is the value that is checked
         * The up scaling can happen only once from the initial unit (Ex: 10^8 W -> 10.000 kW)
         * The down scaling can happen only with the up scaled unit (Ex: 8,999 MWh -> 8999 kWh)
         * This function is defined inside getFormattedValue, because it is the only place it is used
         * @param format the appropriate format from UNITS2FORMATTING
         * @param initialUnit
         * @param upscaledUnit
         * @returns {*} the final format that should be used
         * Side effect: If the threshold for up scaling is surpassed, rawValue of getFormattedValue is divided by 1000
         */
        function unitConversion(format, initialUnit, upscaledUnit) {
            // Set up the hysteresis for the data attribute if it was not set up already
            if (!$scope.hysteresis[dataAttr.code]) {
                $scope.hysteresis[dataAttr.code] = {isUpscaled: false, instances: []}
            }
            if (instance !== null && !$scope.hysteresis[dataAttr.code].instances[instance]) {
                $scope.hysteresis[dataAttr.code].instances[instance] = {isUpscaled: false}
            }

            if (
                rawValue < -10000 || rawValue > 10000 ||
                (rawValue < -9000 || rawValue > 9000) && (
                    instance !== null && $scope.hysteresis[dataAttr.code].instances[instance].isUpscaled ||
                    $scope.hysteresis[dataAttr.code].isUpscaled
                )
            ) {
                // Update hysteresis
                setHysteresis(true)
                // Make conversion
                format = format.replace(initialUnit, upscaledUnit);
                rawValue = rawValue / 1000;
                // Find how many decimals to show; Always show 4 digits
                let decimals = rawValue.toString().split('.')[0].length;
                if (decimals < 4) {
                    format = format.replace(/.\d/, `0.${4 - decimals}`);
                }
            } else {
                setHysteresis(false);
            }
            return format;
        }

        function setHysteresis(isUpscaled) {
            if (instance === null) {
                $scope.hysteresis[dataAttr.code].isUpscaled = isUpscaled
            } else {
                $scope.hysteresis[dataAttr.code].instances[instance].isUpscaled = isUpscaled
            }
        }

        // Edge case for 'enum' values
        if (dataAttr.dataType && dataAttr.dataType == 'enum') {
            const enumVal = _.find(dataAttr.dataAttributeEnumValues, function(record) {
                return record.valueEnum == rawValue;
            });
            return enumVal ? enumVal.nameEnum : null;
        }
        // If the instance is given, but the data attribute does not have such an instance, return invalid value
        if (
            instance !== null &&
            (!dataAttr.instances || !dataAttr.instances[instance])
        ) {
            return INVALID_VALUE
        }

        // Get the right raw value if it was not passed to the function
        if (rawValue === null) {
            // If the instance is not given, use the rawValue of the dataAttr
            if (instance === null) {
                rawValue = dataAttr.rawValue
            }
            // If the instance is given, use the rawValue of the instance
            else {
                rawValue = dataAttr.instances[instance].rawValue;
            }
        }

        // Edge case for 'string' values; They don't have to be checked if they are outdated
        if (dataAttr.dataType && dataAttr.dataType === 'string') {
            return sprintf(dataAttr.formatWithUnit.replace('F', 'f'), rawValue);
        }

        // Check for empty values
        rawValue = $filter('vrmNotApplicable')(rawValue);
        if (rawValue === INVALID_VALUE) {
            return rawValue;
        }

        // Choose the proper timestamp; of dataAttr or of the instance
        let timestamp = null;
        if (instance != null) {
            timestamp = dataAttr.instances[instance].timestamp;
        } else if (dataAttr.timestamp) {
            timestamp = dataAttr.timestamp;
        }

        let exceptionAttributesForOutdatedness = [...TANKS_REFRESH_ATTRIBUTES]
        // Check for outdated values only if timestamp is defined
        if (!exceptionAttributesForOutdatedness.includes(dataAttr.code) && timestamp && isValueOutdated(timestamp)) {
            return INVALID_VALUE;
        }

        // Format the values
        let format = dataAttr.formatWithUnit;
        if (format) {
            // If value is 0, show no decimals
            if (rawValue === 0) {
                format = format.replace(/.\d/, `0.${0}`);
            }
            // SOC is an exception that uses the default format
            else if (dataAttr.code && ["SOC", "bs", "VSH", "H4as", "H4ms", "iSOC", "mSOC", "dems", "dets", "vrm_soc_def", "vrm_soc_plan"].includes(dataAttr.code)) {
                format = UNITS2FORMATTING.SOC;
            }
            // Change formating for units that are not according to Victron's protocol
            else if (format.includes('A')) {
                format = UNITS2FORMATTING.A;
                if (rawValue > 100) format = format.replace(/.\d/, `0.${0}`);
            } else if (format.includes('kWh')) {
                format = unitConversion(UNITS2FORMATTING.kWh, 'kWh', 'MWh');
            } else if (format.includes('Wh')) {
                format = unitConversion(UNITS2FORMATTING.Wh, 'Wh', 'kWh');
            } else if (format.includes('W')) {
                format = unitConversion(UNITS2FORMATTING.W, 'W', 'kW');
            }
            // Check for AC Voltage
            else if (
                format.includes('V') &&
                ((dataAttr.dbusPath && dataAttr.dbusPath.toUpperCase().includes('AC')) ||
                    (dataAttr.dataAttributes &&
                        dataAttr.dataAttributes[0] &&
                        dataAttr.dataAttributes[0].toUpperCase().includes('AC')))
            ) {
                format = UNITS2FORMATTING.VAC;
            }
            // Otherwise the Voltage will use the DC formatting / precision
            else if (format.includes('V')) {
                format = UNITS2FORMATTING.VDC
                if (rawValue > 100) {
                    format = format.replace(/.\d/, `0.${0}`)
                }
            } else if (format.includes('%%')) {
                format = UNITS2FORMATTING.PERCENTAGE;
            }
            return sprintf(format.replace('F', 'f'), rawValue);
        }
        // if format is undefined, return invalid value
        return INVALID_VALUE;
    }

    $scope.calculateCurrent = function (power, voltage, timestamp) {
        if (power === null || voltage === null || power === undefined || voltage === undefined) {
            return INVALID_VALUE;
        }

        let value;
        if (parseFloat(voltage) === 0 || parseFloat(power) === 0) {
            value = 0
        } else {
            value = parseFloat(power) / parseFloat(voltage);
        }

        return getFormattedValue({formatWithUnit:UNITS2FORMATTING.A, timestamp: timestamp}, value);
    }

    $scope.showDetails = localStorageService.get(SHOW_DETAILS_STORAGEKEY);
    $scope.showAll = false;

    // Select visible graph for historical data
    $scope.graphs = {
        choices: [
            { id: 'liveFeed', name: 'live_feed', dataURL: 'stats', type: 'live_feed', available: 'yes' },
            { id: 'consumption', name: 'consumption', dataURL: 'stats', type: 'consumption', available: 'yes' },
            { id: 'solar', name: 'solar', dataURL: 'stats', type: 'solar_yield', available: 'yes' },
            { id: 'generator', name: 'generator', dataURL: 'stats', type: 'generator', available: 'yes' },
            { id: 'grid', name: 'grid', dataURL: 'stats', type: 'grid', available: 'yes' },
            { id: 'evcs', name: 'evcs', dataURL: 'stats', type: 'evcs', available: 'no', show_instance: true },
        ],
        current: null
    };

    $scope.getEvcsSeriesIfSelected = function () {
        if ($scope.graphs.current.id === 'evcs') {
            return $scope.charts.evcs.series;
        }
        return null;
    }

    ApiService.installations($stateParams.id, 'reset-forecasts', {resetType: "solar"}, 'GET').then(function (response) {
        // Get current time in seconds
        const now = Math.floor(Date.now() / 1000);
        const lastReset = response.data.last_reset;
        const threeHours = 3 * 60 * 60;

        $scope.showForecastsUnavailable = (now - lastReset <= threeHours)
    });

    $scope.$watch('ci.evE', function () {
        if ($scope.ci.evE && $scope.ci.evE.instances && Object.keys($scope.ci.evE.instances).length) {
            $scope.graphs.choices.find(g => g.id === 'evcs').available = 'yes';
        }
    });

    // Graph zoomstate
    $scope.zoomstate = { min: null, max: null };
    $scope.toggles = {
        showMapOptions: false
    };
    // Flags get set by the loadData() call
    $scope.hasKwhData = false;
    $scope.hasSolarKwhData = false;
    $scope.hasSoc = false;
    $scope.hasEvCarChargerOut = true;
    $scope.hasEvCarChargerIn = false;
    $scope.hasNewGeneratorPaths = false;
    $scope.hasDynamicEssCapability = false;
    $scope.hasPdcData = false;
    $scope.hasiOI1Data = false;
    $scope.hasBvData = false;
    $scope.datePicker = {
        'range': {},
        'control': {}
    };

    $scope.dessDatePicker = {
        'range': {},
        'control': {}
    };
    $scope.only_grid_meter = false;

    $scope.forecastQuickranges = ['qr_today', 'qr_tomorrow', 'qr_day_after_tomorrow', 'qr_next_7_days', 'qr_next_2_days'];
    $scope.onlyForecastQuickranges = ['qr_tomorrow', 'qr_day_after_tomorrow'];
    // Like onlyForecastQuickranges, but include historic data for the current day
    $scope.mustHaveForecastQuickranges = ['qr_next_2_days', 'qr_next_7_days'];
    $scope.evChargerDataIn = {};
    $scope.evChargerDataOut = {};
    $scope.isEssTypeSystem = false;
    $scope.evChargerShowAll = {
        'in': false,
        'out': false,
    };

    $scope.showFaqAboutCharts = false;
    $scope.showGridHistory = undefined;
    $scope.canHaveForecast = false;
    $scope.canHaveForecastSoon = false;
    $scope.allowForecast = false;
    $scope.showForecastButton = false;
    $scope.onlyForecast = false;
    $scope.mustHaveForecast = false;
    $scope.showForecast = getForecast();

    function getForecast() {
        const forecast = localStorageService.get(SHOW_FORECAST_STORAGEKEY)
        if(forecast === null){
            localStorageService.set(SHOW_FORECAST_STORAGEKEY, true);
            return true;
        }
        return forecast
    }

    $scope.hasSolarForecastData = false;
    $scope.hasConsumptionForecastData = false;
    $scope.tabs = ['energy', 'cost_saving', 'schedule'];
    $scope.activeTab = localStorageService.get(SHOW_GRAPH_TAB_STORAGEKEY) || $scope.tabs[0];
    $scope.forecastShown = false;
    $scope.weatherIcons = {
        'sunny': 'vrm-weather-icons-72px_01d-Sunny',
        'clear-night': 'vrm-weather-icons-72px_01n-clear-night',
        'partially-cloudy': 'vrm-weather-icons-72px_02d-partially-cloudy',
        'partially-cloudy-night': 'vrm-weather-icons-72px_02n-partially-cloudy',
        'cloudy': 'vrm-weather-icons-72px_03d-cloudy',
        'cloudy-night': 'vrm-weather-icons-72px_034-cloudy',
        'clouds': 'vrm-weather-icons-72px_04d-cloudy',
        'clouds-night': 'vrm-weather-icons-72px_04n-cloudy',
        'cloudy-with-rain': 'vrm-weather-icons-72px_10d-rain',
        'cloudy-with-rain-night': 'vrm-weather-icons-72px_10n-rain',
        'partially-cloudy-with-rain': 'vrm-weather-icons-72px_09d-shower-rain', // temporary
        'shower-rain': 'vrm-weather-icons-72px_09d-shower-rain',
        'shower-night': 'vrm-weather-icons-72px_09n-shower-rain',
        'thunderstorm-with-rain': 'vrm-weather-icons-72px_11d-thunderstorm',
        'thunderstorm-with-rain-night': 'vrm-weather-icons-72px_11d-thunderstorm',
        'snow': 'vrm-weather-icons-72px_13d-snow',
        'snow-night': 'vrm-weather-icons-72px_13d-snow',
        'fog': 'vrm-weather-icons-72px_50d-fog',
        'fog-night': 'vrm-weather-icons-72px_50d-fog'
    };
    $scope.weather = {};
    $scope.historyTotals = {};
    $scope.isRealtime = false;
    $scope.realtimeIndicator = REALTIME_INDICATOR.OFFLINE;

    $scope.updateRealtimeIndicator = function (indicator) {
        // trick angular with a microtask to update the realtime indicator
        $timeout(() => {
            $scope.realtimeIndicator = indicator;
            $scope.isRealtime = $scope.realtimeIndicator === REALTIME_INDICATOR.REALTIME;
        })
    };

    $scope.batteries = [];

    $scope.currentInputCurrent = {
        rawValue: "0",
        formattedValue: "0.0 A"
    };

    $scope.isPropertyValid = function (property, withDebug = true) {
        if (withDebug && vrmConfig.debug && SUPPORTED_DEBUG_PROPS.includes(property)) {
            return $scope.debugProps.has(property);
        }
        return $scope.ci && $scope.ci.hasOwnProperty(property) && $scope.ci[property].rawValue != null;
    };

    $scope.isValueValid = function (dataAttribute) {
        return dataAttribute.hasOwnProperty('formattedValue') && dataAttribute.formattedValue !== INVALID_VALUE;
    };

    function hasTheLocationChangedInTheLast3Hours() {
        if($scope.last_location_timestamp === 0) { // is the initial value
            return false;
        }
        const lastLocationChangeDate = moment.unix($scope.last_location_timestamp) // using the last timestamp saved from the API
        return moment().diff(lastLocationChangeDate, 'hours') <= 3;
    }

    $q.all([
        InstallationsService.getInstallation($stateParams.id),
        GraphService.ready(),
        ApiService.dashboard($stateParams.id),
        locale.ready('dashboard'),
    ]).then(async function (result) {
        let updatedAttributes = [];
        let installation = result[0];

        $scope.daysUntilAccurateForecast = $scope.getDaysUntilAccurateForecast(installation.last_location_timestamp);
        $scope.last_location_timestamp = installation.last_location_timestamp ? installation.last_location_timestamp : 0;

        const localForecast = localStorageService.get(HIDE_FORECAST_WARNING_STORAGEKEY_TEMPLATE.replace('*', installation.id))
        $scope.showForecastWarning = !(localForecast && localForecast.hide)

        if($scope.daysUntilAccurateForecast > 0) {
            await updateLastLocationTimestamp(true);
            let forecast = localStorageService.get(SHOW_FORECAST_STORAGEKEY);
            forecast = forecast === null ? true : forecast; // if not in localStorage default to true
            !hasTheLocationChangedInTheLast3Hours() ?
                $scope.dismissForecastWarning() :
                forecast && $scope.activateForecastWarning();
        } else {
            $scope.dismissForecastWarning();
        }

        $scope.dessAccWarningMessage = locale.getString('dashboard.dynamic_ess_accuracy_warning', {
            number: $scope.daysUntilAccurateForecast,
            unit: $scope.daysUntilAccurateForecast === 1 ? locale.getString('common.day') : locale.getString('common.days')
        }, true) || 'In the next days, the forecast will become increasingly accurate.';

        $scope.only_grid_meter = installation && installation.dashboard_variations && installation.dashboard_variations.only_grid_meter;
        $scope.ci = mapDataAttributes(result[2].data.attributes);
        $scope.hasMultipleScS = $scope.ci.ScS && $scope.ci.ScS.instances && Object.keys($scope.ci.ScS.instances).length > 1;
        loadMinMaxTemperatures();
        loadVenusData();
        MQTTRealtimeService.observeRealtime(installation.identifier, $scope.updateRealtimeIndicator);

        if (!InstallationsService.canMaintainRealtimeCommunication()) {
            $scope.isRealtime = false;
            $scope.realtimeIndicator = REALTIME_INDICATOR.OFFLINE;
        }

        function triggerCIRebind(attributes) {
            updatedAttributes = [...updatedAttributes, ...attributes];
            throttledCIRebind();
        }

        // This throttles the UI updates caused by attribute updates. Also, no need for complex
        // watchers, they will be called here.
        const throttledCIRebind = _.throttle(() => {
            const updatesToExecute = [];

            if (includesOneOf(updatedAttributes, MPPT_REFRESH_ATTRIBUTES)) updatesToExecute.push(getMpptsInOrder);
            if (includesOneOf(updatedAttributes, ACTIVE_SOURCE_REFRESH_ATTRIBUTES)) updatesToExecute.push(updateActiveSource);
            if (includesOneOf(updatedAttributes, BATTERY_REFRESH_ATTRIBUTES)) updatesToExecute.push(updateBatteries);
            if (includesOneOf(updatedAttributes, IS_ESS_TYPE_SYSTEM_REFRESH_ATTRIBUTES)) updatesToExecute.push(updateIsEssTypeSystem);
            if (includesOneOf(updatedAttributes, VENUS_GUI_REFRESH_ATTRIBUTES)) updatesToExecute.push(updateShowVenusGUI);
            if (includesOneOf(updatedAttributes, ESS_STATE_ACTIVE_REFRESH_ATTRIBUTES)) updatesToExecute.push(updateEssStateActive);
            if (includesOneOf(updatedAttributes, AC_SOURCES_REFRESH_ATTRIBUTES)) updatesToExecute.push(updateAcSources);
            if (includesOneOf(updatedAttributes, HAS_SUPPOSED_CONTROLS_REFRESH_ATTRIBUTES)) {
                updatesToExecute.push(() => $scope.hasSupposedDeviceControls = hasSupposedControls());
            }
            if (includesOneOf(updatedAttributes, GENERATOR_PATHS_REFRESH_ATTRIBUTES)) updatesToExecute.push(updateCapabilityFlags);
            if (includesOneOf(updatedAttributes, EV_CHARGER_REFRESH_ATTRIBUTES)) updatesToExecute.push(updateEVChargingBlocks);
            if (includesOneOf(updatedAttributes, TEMPERATURE_REFRESH_ATTRIBUTES)) updatesToExecute.push(updateTemperatureBlocks);
            if (includesOneOf(updatedAttributes, TANKS_REFRESH_ATTRIBUTES)) updatesToExecute.push(updateTanks);
            if (includesOneOf(updatedAttributes, CURRENT_LIMIT_REFRESH_ATTRIBUTES)) updatesToExecute.push(updateCurrentLimit);
            if (includesOneOf(updatedAttributes, INPUT_CURRENT_REFRESH_ATTRIBUTES)) updatesToExecute.push(updateInputCurrent);
            if (includesOneOf(updatedAttributes, MULTIPLE_PHASES_REFRESH_ATTRIBUTES)) updatesToExecute.push(updateHasMultiplePhases);
            if (includesOneOf(updatedAttributes, LOCATION_REFRESH_ATTRIBUTES)) {
                updatesToExecute.push(updateLocation);
                updatesToExecute.push(checkCanHaveForecast);
            }

            // Update flows last to make sure any data is updated before we lay out the flows.
            if (includesOneOf(updatedAttributes, FLOWS_REFRESH_ATTRIBUTES)) updatesToExecute.push(updateFlowsAndPosition);

            let exception;
            updatesToExecute.forEach(f => {
                try { f() } catch (e) {
                    console.error(`Error while executing dashboard update ${f.name}`, e);
                    exception = e;
                }
            });

            updatedAttributes = [];
            $scope.$broadcast('$$rebind::ci');

            if (exception) throw exception;
        }, 750);

        function updateBatteries() {
            if ($scope.isPropertyValid('bT')) {
                $scope.batteryTemperature = $scope.ci.bT.formattedValue;
            } else if ($scope.isPropertyValid('abs')) {
                const activeBatteryService = $scope.ci.abs;
                const source = activeBatteryService.rawValue;
                // format: com.victronenergy.battery/512
                const [targetService, targetInstance] = source.split('/');
                $scope.batteryTemperature = ['BT', 'CT', 'ScT']
                    .filter(key => $scope.ci.hasOwnProperty(key))
                    .map(key => $scope.ci[key])
                    .filter(attribute => 'com.victronenergy.' + attribute.dbusServiceType === targetService)
                    .filter(attribute => 'instances' in attribute)
                    .filter(attribute => attribute.instances[targetInstance] &&
                        attribute.instances[targetInstance].rawValue != null &&
                        parseInt(attribute.instances[targetInstance].rawValue) !== -273) // elnino.support/ticket/79350
                    .map(attribute => getFormattedValueForInstance(attribute, targetInstance))
                    .find(() => true);
            } else {
                $scope.batteryTemperature = null;
            }

            if ($scope.isPropertyValid('Ba') && Array.isArray($scope.ci.Ba.rawValue)) {
                $scope.batteries = [];
                $scope.ci.Ba.rawValue.forEach(b => {
                    let timeToGo = b.timetogo;
                    if (timeToGo){
                        switch (typeof timeToGo) {
                            case "number":
                                timeToGo = getFormattedValue($scope.ci.bt, timeToGo / 3600);
                                break;
                            case "string":
                                const maybeNumber = Number(timeToGo); // if the string it's just a number then convert
                                timeToGo = maybeNumber ? getFormattedValue($scope.ci.bt, maybeNumber / 3600) : timeToGo;
                                break;
                            default:
                                break;
                        }
                    }
                    let battery = {
                        name: b.name,
                        power: b.power ? getFormattedValue($scope.ci.bp, b.power) : undefined,
                        current: b.current ? getFormattedValue($scope.ci.bc, b.current) : undefined,
                        voltage: b.voltage ? getFormattedValue($scope.ci.bv, b.voltage) : undefined,
                        state: typeof b.state !== 'undefined' && b.state !== null ? getFormattedValue($scope.ci.bst, b.state) : undefined,
                        soc: b.soc ? getFormattedValue($scope.ci.bs, b.soc) : undefined,
                        timetogo: timeToGo,
                    };
                    if (true && !b.active_battery_service) {
                        $scope.batteries.push(battery);
                    }
                });

                // Fix table display on desktop, easier than fiddling with borders on a lot of places
                if ($scope.batteries.length % 2 !== 0) {
                    $scope.batteries.push({});
                }
            }

            if ($scope.hasSoc && $scope.isPropertyValid('bs')) {
                const timestamp = $scope.charts.liveFeed.series[2].data[$scope.charts.liveFeed.series[2].data.length - 1][0];
                const newValue = Number($scope.ci.bs.rawValue);
                $scope.charts.liveFeed.series[8].data = [[timestamp, newValue], [timestamp, newValue]];
            }
        }


        function updateActiveSource() {
            let inputType = null;
            let si1 = null;
            let si2 = null;

            if ($scope.isPropertyValid('si1') && $scope.ci.si1.rawValue) {
                si1 = Number.parseInt($scope.ci.si1.rawValue);
            }
            if ($scope.isPropertyValid('si2') && $scope.ci.si2.rawValue) {
                si2 = Number.parseInt($scope.ci.si2.rawValue);
            }

            if(si1 && si2) { // not null and not 0 (Not available)
                if ($scope.isPropertyValid('AIS') && $scope.ci.AIS.rawValue) {
                    inputType = Number.parseInt($scope.ci.AIS.rawValue);
                } else {
                    inputType = si1;
                }
            } else if (si1) { // just si1
                inputType = si1;
            } else if (si2) { // just si2
                inputType = si2;
            } else { // no value
                if ($scope.isPropertyValid('AIS') && $scope.ci.AIS.rawValue) {
                    inputType = Number.parseInt($scope.ci.AIS.rawValue);
                }
            }

            switch (inputType) {
                case 1: // grid
                    $scope.activeSource = 'grid';
                    $scope.acInput = 'grid';
                    break;
                case 2: // genset
                    $scope.activeSource = 'genset';
                    $scope.acInput = 'genset';
                    break;
                case 3: // shore
                    $scope.activeSource = 'shore';
                    $scope.acInput = 'grid';
                    break;
                case 240: // inverting - AIS
                    $scope.activeSource = 'unknown';
                    $scope.acInput = $scope.ci.from_to_grid.rawValue === null ? 'unknown' : 'grid';
                    break;
                default:
                    $scope.activeSource = 'unknown';
                    $scope.acInput = 'unknown';
                    break;
            }
        }

        function updateIsEssTypeSystem() {
            const st = typeof $scope.ci.st !== 'undefined' && ($scope.ci.st.rawValue === 'ESS' || $scope.ci.st.rawValue === 'Hub-4');
            const aM = typeof $scope.ci.aM !== 'undefined';

            const dea = typeof $scope.ci.dea !== 'undefined' && +$scope.ci.dea.rawValue === 1;

            // ESS uses st; AC System ESS uses aM;
            $scope.isEssTypeSystem = st || aM;
            $scope.dynamicEssAvailable = dea;
        }

        function updateShowVenusGUI() {
            $scope.showVenusGUI = $scope.isPropertyValid('shao')
            if ($scope.showVenusGUI) {
                if ($scope.ci.rwgm.rawValue == 1) {
                    $scope.isParallel = false;
                } else {
                    $scope.isParallel = installation && (($scope.ci.st.rawValue === 'ESS' || $scope.ci.st.rawValue === 'Hub-4') || $scope.ci.AIT.rawValue === 'grid');
                }
                $scope.hasAcSolarOnIn1 = $scope.isPropertyValid('Pj') || $scope.isPropertyValid('Pj2') || $scope.isPropertyValid('Pj3');
                $scope.hasAcSolarOnIn2 = $scope.isPropertyValid('Pi') || $scope.isPropertyValid('Pi2') || $scope.isPropertyValid('Pi3');
                $scope.hasAcSolarOnOut = $scope.isPropertyValid('P') || $scope.isPropertyValid('P2') || $scope.isPropertyValid('P3');
                $scope.hasAcOutSystem = $scope.isPropertyValid('shao') && $scope.ci.shao.rawValue == '1';
                $scope.hasAcSolarOnIn = $scope.hasAcSolarOnIn1 || $scope.hasAcSolarOnIn2;
                $scope.hasAcSolar = $scope.hasAcSolarOnIn || $scope.hasAcSolarOnOut;
                $scope.hasDcAndAcSolar = $scope.hasAcSolar && ($scope.isPropertyValid('Pdc'));
                $scope.coupledPvAcVisible = $scope.hasDcAndAcSolar || ($scope.hasAcSolarOnIn && $scope.hasAcSolarOnOut) || ($scope.hasAcSolarOnIn1 && $scope.hasAcSolarOnIn2);
                $scope.hasDcPower = (!$scope.isParallel) && $scope.isPropertyValid('shd') && $scope.ci.shd.rawValue == 1;
                $scope.hubInverterSource = $scope.coupledPvAcVisible ? 'hub_inverter' : ($scope.hasAcSolarOnOut ? 'ac_out' : ($scope.hasAcSolarOnIn1 ? 'ac_genset' : 'ac_in'));
                let hasAtLeastOneGeneratorInput = ($scope.isPropertyValid('si1') && $scope.ci.si1.textValue === "Generator") || ($scope.isPropertyValid('si2') && $scope.ci.si2.textValue === "Generator");
                let hasAtLeastOneGridInput = ($scope.isPropertyValid('si1') && $scope.ci.si1.textValue === "Grid") || ($scope.isPropertyValid('si2') && $scope.ci.si2.textValue === "Grid");
                let hasAtLeastOneShoreInput = ($scope.isPropertyValid('si1') && $scope.ci.si1.textValue === "Shore power") || ($scope.isPropertyValid('si2') && $scope.ci.si2.textValue === "Shore power");
                $scope.hasHubGenerator = !$scope.only_grid_meter && ($scope.acInput === 'genset' || $scope.isPropertyValid('gs1') || hasAtLeastOneGeneratorInput)
                $scope.hasHubAcIn = $scope.acInput === 'grid' || hasAtLeastOneShoreInput || hasAtLeastOneGridInput || !$scope.hasHubGenerator;
                const isSystemStateInverting = ($scope.isPropertyValid('ss') && $scope.ci.ss.rawValue == '9'); // DBus: /SystemState/State == "Inverting"
                const isGeneratorActive = $scope.ci.generator.rawValue > 0;
                const isAcInActive = $scope.ci.from_to_grid.rawValue > 0 || $scope.ci.from_to_grid.rawValue < 0;

                $scope.hubAcInSource = isGeneratorActive ? 'genset' : (isSystemStateInverting || isAcInActive ? 'ac_in' : undefined);
                $scope.hasDcPower = (!$scope.isParallel) && $scope.isPropertyValid('shd') && $scope.ci.shd.rawValue == 1;
            }
            $scope.showSolarYield = $scope.isPropertyValid('solar_yield') && (!$scope.showVenusGUI || ($scope.hasAcSolarOnIn1 + $scope.hasAcSolarOnIn2 + $scope.hasAcSolarOnOut + $scope.isPropertyValid('Pdc')) > 1);

            $scope.isAcGenerator = +$scope.ci.si1.rawValue === 2 || +$scope.ci.si2.rawValue === 2 || +$scope.ci.AIS.rawValue === 2;
            const isGridSet = isGrid($scope.ci.si1, $scope.ci.si2, $scope.ci.AIS);
            const isShoreSet = isShore($scope.ci.si1, $scope.ci.si2, $scope.ci.AIS);
            $scope.isAcGrid = isGridSet || isShoreSet;
            if (isGridSet && isShoreSet) {
                $scope.graphs.choices[4].name = "grid_shore";
            } else if (isGridSet) {
                $scope.graphs.choices[4].name = "grid";
            } else if (isShoreSet) {
                $scope.graphs.choices[4].name = "shore";
            }
            $scope.only_grid_meter = $scope.only_grid_meter && !$scope.hasAcSolar;
            $scope.loading = false;
        }

        function updateEssStateActive() {
            const ess = []
            const { ssls, ssbl, ssdd, ssdc, sssc, ssuc, ssud } = $scope.ci;
            const isEssStateActive = (prop) => {
                return prop && prop.rawValue !== undefined && prop.rawValue == '1';
            }
            isEssStateActive(ssls) && ess.push(1); // #1 SOC is low
            isEssStateActive(ssbl) && ess.push(2); // #2 BatteryLife is active
            isEssStateActive(ssdc) && ess.push(3); // #3 Charge disabled by BMS
            isEssStateActive(ssdd) && ess.push(4); // #4 Discharge disabled by BMS
            isEssStateActive(sssc) && ess.push(5); // #5 Slow charge is active
            isEssStateActive(ssuc) && ess.push(6); // #6 Charge disabled by user settings
            isEssStateActive(ssud) && ess.push(7); // #7 Discharge disabled by user settings
            $scope.ess = ess;
        }

        // New way of detecting AC Sources since Venus 2.80
        // Used only to show details in the dashboard for now
        // https://github.com/victronenergy/vrm-portal/issues/231
        function updateAcSources() {
            // Use `vi0` to detect if the installation supports the new way of detecting AC Sources
            if ($scope.isPropertyValid('vi0') && $scope.isPropertyValid('ini')) {
                $scope.useDetailsFromServices = true;
                $scope.acSources = $scope.ci.ini.rawValue
            } else {
                $scope.useDetailsFromServices = false;
            }

            $scope.acSource0 = {
                connected: $scope.isPropertyValid('ic0') ? +$scope.ci.ic0.rawValue : undefined,
                instance: $scope.isPropertyValid('id0') ? +$scope.ci.id0.rawValue : undefined,
                vrmInstance: $scope.isPropertyValid('vi0') ? +$scope.ci.vi0.rawValue : undefined,
                serviceType: $scope.isPropertyValid('ist0') ? $scope.ci.ist0.rawValue : undefined,
                source: $scope.isPropertyValid('is0') ? $scope.ci.is0.rawValue : undefined,
            }

            $scope.acSource1 = {
                connected: $scope.isPropertyValid('ic1') ? +$scope.ci.ic1.rawValue : undefined,
                instance: $scope.isPropertyValid('id1') ? +$scope.ci.id1.rawValue : undefined,
                vrmInstance: $scope.isPropertyValid('vi1') ? +$scope.ci.vi1.rawValue : undefined,
                serviceType: $scope.isPropertyValid('ist1') ? $scope.ci.ist1.rawValue : undefined,
                source: $scope.isPropertyValid('is1') ? $scope.ci.is1.rawValue : undefined,
            }

            // Select the first AC Source marked as 'Connected'
            $scope.acSource = $scope.acSource0.connected ? $scope.acSource0 : ($scope.acSource1.connected ? $scope.acSource1 : undefined);

            if ($scope.acSource) {
                // Get voltage, current and power for the connected AC Source instance
                const instance = $scope.acSource.vrmInstance;
                switch ($scope.acSource.serviceType) {
                    case 'vebus':
                        if ($scope.isPropertyValid('IV1')) {
                            $scope.acSource.L1 = {
                                voltage: updateFormattedValue($scope.ci.IV1, instance),
                                current: updateFormattedValue($scope.ci.II1, instance),
                                power: updateFormattedValue($scope.ci.IP1, instance)
                            };
                        }
                        if ($scope.isPropertyValid('IV2')) {
                            $scope.acSource.L2 = {
                                voltage: updateFormattedValue($scope.ci.IV2, instance),
                                current: updateFormattedValue($scope.ci.II2, instance),
                                power: updateFormattedValue($scope.ci.IP2, instance)
                            }
                        }
                        if ($scope.isPropertyValid('IV3')) {
                            $scope.acSource.L3 = {
                                voltage: updateFormattedValue($scope.ci.IV3, instance),
                                current: updateFormattedValue($scope.ci.II3, instance),
                                power: updateFormattedValue($scope.ci.IP3, instance)
                            }
                        }
                        break;
                    case 'grid':
                        if ($scope.isPropertyValid('g1v')) {
                            $scope.acSource.L1 = {
                                voltage: updateFormattedValue($scope.ci.g1v, instance),
                                current: updateFormattedValue($scope.ci.g1c, instance),
                                power: updateFormattedValue($scope.ci.g1p, instance)
                            };
                        }
                        if ($scope.isPropertyValid('g2v')) {
                            $scope.acSource.L2 = {
                                voltage: updateFormattedValue($scope.ci.g2v, instance),
                                current: updateFormattedValue($scope.ci.g2c, instance),
                                power: updateFormattedValue($scope.ci.g2p, instance)
                            };
                        }
                        if ($scope.isPropertyValid('g3v')) {
                            $scope.acSource.L3 = {
                                voltage: updateFormattedValue($scope.ci.g3v, instance),
                                current: updateFormattedValue($scope.ci.g3c, instance),
                                power: updateFormattedValue($scope.ci.g3p, instance)
                            };
                        }
                        break;
                    case 'genset':
                        if ($scope.isPropertyValid('G1v')) {
                            $scope.acSource.L1 = {
                                voltage: updateFormattedValue($scope.ci.G1v, instance),
                                current: updateFormattedValue($scope.ci.G1c, instance),
                                power: updateFormattedValue($scope.ci.G1p, instance)
                            };
                        }
                        if ($scope.isPropertyValid('G2v')) {
                            $scope.acSource.L2 = {
                                voltage: updateFormattedValue($scope.ci.G2v, instance),
                                current: updateFormattedValue($scope.ci.G2c, instance),
                                power: updateFormattedValue($scope.ci.G2p, instance)
                            };
                        }
                        if ($scope.isPropertyValid('G3v')) {
                            $scope.acSource.L3 = {
                                voltage: updateFormattedValue($scope.ci.G3v, instance),
                                current: updateFormattedValue($scope.ci.G3c, instance),
                                power: updateFormattedValue($scope.ci.G3p, instance)
                            };
                        }
                        break;
                    case 'acsystem':
                        if ($scope.isPropertyValid('aIV1')) {
                            $scope.acSource.L1 = {
                                voltage: updateFormattedValue($scope.ci.aIV1, instance),
                                current: updateFormattedValue($scope.ci.aII1, instance),
                                power: updateFormattedValue($scope.ci.aIP1, instance)
                            };
                        }
                        if ($scope.isPropertyValid('aIV2')) {
                            $scope.acSource.L2 = {
                                voltage: updateFormattedValue($scope.ci.aIV2, instance),
                                current: updateFormattedValue($scope.ci.aII2, instance),
                                power: updateFormattedValue($scope.ci.aIP2, instance)
                            };
                        }
                        if ($scope.isPropertyValid('aIV3')) {
                            $scope.acSource.L3 = {
                                voltage: updateFormattedValue($scope.ci.aIV3, instance),
                                current: updateFormattedValue($scope.ci.aII3, instance),
                                power: updateFormattedValue($scope.ci.aIP3, instance)
                            };
                        }
                        break;
                }
            }

            $scope.generatorRunReasonCode = $scope.isPropertyValid('gRC') ? +$scope.ci.gRC.rawValue : undefined;
            $scope.generatorLastStarted = $scope.isPropertyValid('gLS') ? +$scope.ci.gLS.rawValue * 1000 : undefined;
            $scope.generatorRuntime = $scope.isPropertyValid('gRT') ? +$scope.ci.gRT.rawValue : undefined;
            $scope.generatorAccumulatedRuntime = $scope.isPropertyValid('Gt') ? +$scope.ci.Gt.rawValue : undefined;

            $scope.$broadcast('$$rebind::acSource');
            $scope.$broadcast('$$rebind::generator');
        }

        function updateFlowsAndPosition() {
            updateFlows();
            $scope.$broadcast('$$rebind::flows');
            setTimeout($scope.positionLines, 20);
        }

        let mqttInterval = null;

        function subscribeMqtt() {
            if (!InstallationsService.canMaintainRealtimeCommunication()) return;
            MQTTRealtimeService.subscribe();

            const copyCi = { ...$scope.ci };
            let changedLastTick = {};
            _.each(copyCi, function (record) {
                if (!record.dbusPath && !record.dataAttributes) return;
                record.firstMessageReceived = false;
                record.lastMqttValues = {};
                record.mqttFetcher = _.bind(function (message) {
                    // Skip formatting if payload value is null
                    if (message.payload.value === null) return;

                    this.lastMqttValues[message.destinationName] = MQTTRealtimeService.convertValue(this.code, message.payload.value);
                    let value = { rawValue: null };
                    let instancedValue = { rawValue: this.lastMqttValues[message.destinationName] };
                    // Sum our value
                    _.each(this.lastMqttValues, function (lastMqttVal) {
                        value.rawValue = (typeof lastMqttVal === "number" && record.dataType !== "enum") ? (value.rawValue + lastMqttVal) : lastMqttVal;
                    });

                    let formattedValue = null;
                    let formattedInstancedValue = null;
                    let instance = null;
                    try {
                        formattedValue = getFormattedValue(record, value.rawValue);
                        if (record.hasOwnProperty('instances')) {
                            // For CCGX devices the VRM instance of VE.Bus paths is 0, so we assign the value to that instance
                            instance = (record.dbusServiceType === 'vebus' && this.instances.hasOwnProperty(0))
                                ? 0 : MQTTRealtimeService.parseInstanceFromPath(message.destinationName);
                            if (instance !== null) {
                                formattedInstancedValue = getFormattedValue(record, instancedValue.rawValue, instance);
                            }
                        }
                    } catch (e) {
                        console.error('Error while formatting value', value.rawValue);
                        return;
                    }
                    if (formattedValue !== null)
                        value.formattedValue = formattedValue;
                    if (formattedInstancedValue !== null)
                        instancedValue.formattedValue = formattedInstancedValue;
                    if (record.dataType && record.dataType === "enum" && formattedValue !== null)
                        value.textValue = formattedValue;
                    if (record.dataType && record.dataType === "enum" && formattedInstancedValue !== null)
                        instancedValue.textValue = formattedInstancedValue;
                    if (record.hasOwnProperty('instances')) {
                        if (this.instances.hasOwnProperty(instance)) {
                            Object.assign(this.instances[instance], instancedValue)
                        } else {
                            console.error('Trying to set unknown instance')
                        }
                    }
                    Object.assign(this, value);
                    changedLastTick[this.code] = true;
                    this.firstMessageReceived = true;
                }, record);

                if (vrmConfig.enableRealtime) {
                    let arrAttributes = record.dbusPath ? [record] : record.dataAttributes;
                    if (arrAttributes.length === 1 && record.hasOwnProperty('instances')) {
                        arrAttributes = Object.entries(record.instances).map(([instance, el]) => {
                            return {
                                dbusServiceType: record.dbusServiceType,
                                dbusPath: record.dbusPath,
                                instance: instance
                            }
                        })
                    }
                    _.each(arrAttributes, function (attribute) {
                        if (typeof attribute.instance === "undefined") return;
                        MQTTRealtimeService.subscribeToPath({
                            identifier: $scope.installation.identifier,
                            dbusServiceType: attribute.dbusServiceType,
                            dbusPath: attribute.dbusPath,
                            // For CCGX devices, the VE.Bus instance is shown (incorrectly) as 0 in vrm. Since there should
                            // only be one such instance, subscribe to it via the + wildcard.
                            instance: attribute.dbusServiceType === 'vebus' ? '+' : attribute.instance,
                            callback: (msg) => {
                                //Pass code+dbusPath as argument to differentiate by individual subscription in buffer map
                                record.mqttFetcher && record.mqttFetcher(msg);
                            },
                            callbackIdentifier: "dashboard_" + record.code
                        }, true);
                    });

                }

            });
            clearInterval(mqttInterval);
            mqttInterval = setInterval(function () {
                triggerCIRebind(Object.keys(changedLastTick));
                changedLastTick = {};
                const cp = { ...copyCi };
                Object.keys(cp).forEach((key) => {
                    delete cp.mqttFetcher;
                    delete cp.lastMqttValues;
                });
                $scope.ci = copyCi;
            }, 2000);
        }

        /**
         * Maps dashboard data attributes to different formats
         */
        function mapDataAttributes(attributes) {
            if (!attributes) {
                // no attributes to map
                return [];
            }
            for (let k of Object.keys(attributes)) {
                attributes[k].formattedValue = getFormattedValue(attributes[k]);
                if (attributes[k].instances && attributes[k].dataType === 'float' && attributes[k].formatWithUnit) {
                    for (let i of Object.keys(attributes[k].instances)) {
                        attributes[k].instances[i].formattedValue = getFormattedValueForInstance(attributes[k], i)
                    }
                }
            }
            return attributes;
        }

        function unsubscribeMqtt() {
            MQTTRealtimeService.unsubscribe();
        }

        $scope.$watch(function () {
            return installation && installation.identifier && InstallationsService.canMaintainRealtimeCommunication()
        }, (isRealtime) => {
            if (!isRealtime) {
                unsubscribeMqtt();
            } else {
                subscribeMqtt();
            }
        });

        function subscribeIfConnected() {
            if (MQTTRealtimeService.connected) {
                subscribeMqtt();
                return true;
            }
            return false;
        }

        function refreshInstallation() {
            if (document.hidden || !navigator.onLine) {
                return;
            }
            ApiService.dashboard($stateParams.id).then(function (result) {
                $scope.ci = mapDataAttributes(result.data.attributes);
                triggerCIRebind(Object.keys(result.data.attributes));
                loadMinMaxTemperatures();
                subscribeMqtt();
            });
        }

        $scope.refreshInstallation = refreshInstallation;

        // Set up the refresh timer
        const refreshInterval = 60 * 1000;
        let refreshTimer = $interval(refreshInstallation, refreshInterval);

        // Resubscription timer
        const resubscriptionInterval = 1000;
        let resubscriptionTimer = null;

        // Set up the chart updater timer
        const chartUpdateInterval = 5 * 60 * 1000;
        let chartUpdater = $interval(updateCharts, chartUpdateInterval);

        const pageVisibilityRootScope = $rootScope.$on('page-visibility-change', function (_, isVisible) {
            if (isVisible) {
                $scope.refreshInstallation();
                refreshTimer = $interval(refreshInstallation, refreshInterval);

                updateCharts();
                chartUpdater = $interval(updateCharts, chartUpdateInterval);

                if (!subscribeIfConnected()) {
                    resubscriptionTimer = $interval(function () {
                        subscribeIfConnected() && $interval.cancel(resubscriptionTimer);
                    }, resubscriptionInterval);
                }
            } else {
                if (resubscriptionTimer) {
                    $interval.cancel(resubscriptionTimer);
                }

                if (chartUpdater) {
                    $interval.cancel(chartUpdater);
                }

                if (refreshTimer) {
                    $interval.cancel(refreshTimer);
                }
            }
        })

        // Clean up when the scope is destroyed
        $scope.$on("$destroy", function () {
            clearInterval(mqttInterval);
            // Destroy root scope listener
            pageVisibilityRootScope();

            if (resubscriptionTimer) {
                $interval.cancel(resubscriptionTimer);
            }

            if (refreshTimer) {
                $interval.cancel(refreshTimer);
            }

            if (chartUpdater) {
                $interval.cancel(chartUpdater);
            }
            throttledCIRebind.cancel();
        });


        // Fetch weather information
        ApiService.installations($stateParams.id, 'weather').then(function (response) {
            $scope.weather = response.data.records;
        });

        GraphService.highcharts.setOptions({
            global: {
                timezoneOffset: -installation.timezone_offset / 60,
                timezone: installation.timezone
            }
        });

        $scope.map = false;

        // used for debug purposes
        if (vrmConfig.debug) {
            $scope.$watchGroup(['ci.P.rawValue', 'ci.Pdc.rawValue', 'ci.Pi.rawValue'], function () {
                const { debugProps, isPropertyValid } = $scope;
                SUPPORTED_DEBUG_PROPS.forEach((prop) => isPropertyValid(prop, false) && debugProps.add(prop));
            })
        }

        $scope.toggleValidProp = function (property) {
            if (vrmConfig.debug) {
                const { debugProps } = $scope;
                if (debugProps.has(property)) {
                    debugProps.delete(property);
                } else {
                    debugProps.add(property);
                }
                updateFlows()
            }
        }

        $scope.debuggedProperty = function (property) {
            return $scope.debugProps.has(property);
        }

        function isGrid(si1, si2, AIS) {
            return +si1.rawValue === 1 || +si2.rawValue === 1 || +AIS.rawValue === 1;
        }

        function isShore(si1, si2, AIS) {
            return +si1.rawValue === 3 || +si2.rawValue === 3 || +AIS.rawValue === 3;
        }

        $scope.$watch(function () {
            return {
                evChargherShowAll: $scope.evChargherShowAll,
                chartHovers: $scope.chartHovers
            }
        }, () => {
            setTimeout($scope.positionLines, 20);
        }, true);

        $scope.currentInputLimit = null;
        function updateCurrentLimit() {
            $scope.currentInputLimit =
                get($scope.ci, "AILIM.formattedValue", null) ||
                get($scope.ci, "mIL.formattedValue", null);
            const mILInstances = sortBy(
                Object.values(get($scope.ci, "mIL.instances", [])),
                "timestamp"
            );

            if (!$scope.currentInputLimit && mILInstances.length) {
                $scope.currentInputLimit =
                    mILInstances[mILInstances.length - 1].formattedValue;
            }
        }

        function updateInputCurrent() {
            if (!$scope.ci || !($scope.ci.II1 && $scope.ci.II2 && $scope.ci.II3)) {
                // No values for this installation, graphs do not have to be updated
                return;
            }
            $scope.currentInputCurrent =
                ($scope.ci.II1.rawValue > $scope.ci.II2.rawValue && $scope.ci.II1.rawValue > $scope.ci.II3.rawValue)
                    ? updateFormattedValue($scope.ci.II1) : ($scope.ci.II2.rawValue > $scope.ci.II3.rawValue
                        ? updateFormattedValue($scope.ci.II2) : updateFormattedValue($scope.ci.II3));

            let fraction = $scope.currentInputCurrent.rawValue / $scope.ci.AILIM.rawValue * 100;
            $scope.charts.current_limit.series[0].data[0].y = fraction;
            $scope.charts.current_limit.series[0].data[1].y = 100 - fraction;
            $scope.charts.current_limit.loading = false;
        }

        $scope.openHistoryInfoModal = function () {
            VrmModalService.openVueModal(HistoryInfoModal);
        }

        $scope.setActiveTab = function (tab) {
            $scope.activeTab = tab;
            localStorageService.set(SHOW_GRAPH_TAB_STORAGEKEY, $scope.activeTab);
        }

        $scope.getEssAlgorithmGraphItem = function (id) {
            return $scope.charts.essAlgorithm.series.find(item => item.id === id) || {};
        }
        $scope.toggleEssAlgorithmGraphItem = function (id) {
            const item = $scope.getEssAlgorithmGraphItem(id);
            const item_fc = $scope.getEssAlgorithmGraphItem(id + '_fc');
            const visibility = item.visible || item_fc.visible;
            item.visible = !visibility;
            item_fc.visible = !visibility;
        }

        $scope.$watchGroup([
            'graphs.current.id',
            'datePicker.range.name',
            'installation.last_location_timestamp'
        ], checkCanHaveForecast);

        function updateLocation () {
            updateLastLocationTimestamp() // check if location changed more than 10km
            checkCanHaveForecast() // check to see if we hide forecast
        }


        async function updateLastLocationTimestamp(blockNextCall = false) {
            if ($scope.blockNextCacheLocationCall){
                $scope.blockNextCacheLocationCall = false;
                return;
            }
            if(blockNextCall){
                $scope.blockNextCacheLocationCall = true;
            }
            // if the location changed less the 10km then a older timestamp is in place
            const result = await $scope.getCorrectLastLocationTimestamp($scope.installation.id);
            $scope.last_location_timestamp = result ? result : 0;
            // update daysUntilAccurateForecast with the new timestamp
            $scope.daysUntilAccurateForecast = $scope.getDaysUntilAccurateForecast($scope.last_location_timestamp);
        }

        function checkCanHaveForecast() {
            if (!isDateRangeForecastAllowed()) {
                $scope.canHaveForecast = false;
                $scope.canHaveForecastSoon = false;
                return false;
            }

            if ($scope.graphs.current.id !== 'liveFeed') {
                $scope.canHaveForecast = false;
                $scope.canHaveForecastSoon = false;
                return false;
            }

            const requiredDataAttributes = [
                'pP1', 'pP2', 'pP3', // PV Inverters
                'PVP', 'PVP0', 'PVP1', 'PVP2', 'PVP3', // Solar chargers
                'mPVP', 'mPVP0', 'mPVP1', 'mPVP2', 'mPVP3', // MultiRS
                'iPVP', 'iPVP0', 'iPVP1', 'iPVP2', 'iPVP3', // InverterRS
                'consumption', 'consumption_input', 'consumption_output', // Consumption
            ];

            // Check if any of the required data attributes has data
            const hasForecastableData = requiredDataAttributes.some((attribute) => {
                const hasValue = $scope.ci[attribute] && $scope.ci[attribute].rawValue !== null;
                const hasInstances = !!($scope.ci[attribute] && $scope.ci[attribute].instances && $scope.ci[attribute].instances.length > 0);
                return hasValue || hasInstances;
            });

            if (!hasForecastableData) {
                $scope.canHaveForecast = false;
                $scope.canHaveForecastSoon = false;
                return false;
            }

            $scope.canHaveForecast = true;

            if ($scope.ci.lt && $scope.ci.lg && $scope.ci.lt.rawValue && $scope.ci.lg.rawValue) {
                $scope.canHaveForecastSoon = true;
            }

            return $scope.canHaveForecastSoon && $scope.canHaveForecast && !hasTheLocationChangedInTheLast3Hours();
        }

        $scope.onlyShowForecast = function () {
            if ($scope.datePicker.range.name && $scope.datePicker.range.name !== "") {
                return $scope.datePicker.range.name !== "qr_today"
                    ? $scope.forecastQuickranges.includes($scope.datePicker.range.name)
                    : false; // show total and forecast for Today;
            }

            const now = Date.now() / 1000;
            if ($scope.datePicker.range.startDate > now) { // startDate in the Future, so we only have forcast
                return true;
            }
            if ($scope.datePicker.range.endDate < now) { // endDate is in the past, so we have the Total value
                return false;
            }
            return false;
        }

        $scope.toggleForecast = function (status = null, forceReload = true, persist = true) {

            if (!$scope.allowForecast) {
                status = false;
            }
            // Do not reload if not necessary
            if (!forceReload && status === $scope.showForecast && status === $scope.forecastShown) {
                return;
            }

            // Toggle and remember
            $scope.showForecast = status !== null ? status : !$scope.showForecast;
            if (persist)
                localStorageService.set(SHOW_FORECAST_STORAGEKEY, $scope.showForecast);



            if ($scope.allowForecast) {
                // Check if forecast data is available before we allow it
                const startDate = $scope.datePicker.range.startDate;
                const endDate = $scope.datePicker.range.endDate;
                const interval = $scope.datePicker.range.interval;
                GraphService.getGraphData(
                    $stateParams.id,
                    startDate,
                    endDate,
                    interval,
                    "stats",
                    { type: "forecast" }
                ).then(data => {
                    if (data.records["solar_yield_forecast"] === false && data.records["vrm_consumption_fc"] === false) {
                        $scope.allowForecast = false;
                        $scope.showForecast = false;
                    }

                    if ($scope.showForecast) {
                        enableForecasts(data);
                    } else {
                        disableForecasts(
                            $scope.showForecast !== $scope.forecastShown
                        );
                    }
                });
                if (
                    $scope.dynamicEssIsOn &&
                    $scope.dynamicEssAvailable
                ) {
                    GraphService.getGraphData(
                        $stateParams.id,
                        startDate,
                        endDate,
                        interval,
                        "stats",
                        { type: "dynamic_ess" }
                    ).then(data => {
                        if (
                            !data.records ||
                            !data.records.vrm_soc_plan ||
                            data.records.vrm_soc_plan.length === 0
                        )
                            return;
                        $scope.charts.liveFeed.series[12].visible = false;
                        $scope.charts.liveFeed.series[12].data = [];
                        if (
                            !$scope.showForecast ||
                            !$scope.datePicker.range.name ||
                            ![
                                "qr_today",
                                "qr_day_after_tomorrow",
                                "qr_next_2_days",
                                "qr_tomorrow"
                            ].includes($scope.datePicker.range.name)
                        )
                            return;
                        // Remove points in the past
                        const [from, to] = nowIndicatorInterval();
                        const targetSocSeries = data.records.vrm_soc_plan.filter(
                            point => point[0] >= from + (to - from) / 2 // now is in the middle between from and to
                        );
                        // Cap values to 100% SOC
                        targetSocSeries.forEach(point => {
                            point[1] = Math.min(100, point[1]);
                        });
                        EssGraphService.addLiveFeedForecast(
                            $scope.charts.liveFeed,
                            targetSocSeries,
                            12,
                            EssGraphService.colors.BLUE,
                            $scope.onlyForecast,
                            from,
                            to
                        );
                        $scope.charts.liveFeed.series[12].visible =
                            $scope.isEntryActive["battery"];
                    });
                }
            } else {
                disableForecasts($scope.showForecast !== $scope.forecastShown);
            }
        }

        function updateHasMultiplePhases() {
            const hasMultiplePhases = $scope.ci.PC && $scope.ci.PC.rawValue && (+$scope.ci.PC.rawValue > 1);
            $scope.showHistoryInfoModal = $scope.isPropertyValid('aps1') && hasMultiplePhases;
        }

        function isDateRangeForecastAllowed() {
            if ($scope.datePicker.range.name) {
                return $scope.forecastQuickranges.includes($scope.datePicker.range.name);
            }

            return $scope.datePicker.range.endDate > siteNow();

        }

        function enableForecasts(data) {
            if ($scope.forecastLoading)
                return;

            if ($scope.onlyForecast) {
                // Reset all history tiles because we have no data available
                Object.keys($scope.historyTotals).forEach(function (key) {
                    if (!['consumption_forecast_remaining', 'solar_forecast_remaining'].includes(key))
                        $scope.historyTotals[key] = '';
                })

                $scope.historyTotals.toGrid = '0';
                $scope.historyTotals.fromGrid = '0';
                $scope.historyTotals.totalConsumption = '0';
                $scope.historyTotals.totalProduction = '0';
            }

            $scope.showForecastButton = true;
            const calcRemainingForecast = (forecastSeries) => {
                const nowTimestamp = +new Date();
                const periodLength = forecastSeries[1][0] - forecastSeries[0][0];
                return forecastSeries.reduce((total, point) => {
                    if (point[0] > nowTimestamp) {
                        return total + point[1] / 1000;
                    } else if (point[0] + periodLength > nowTimestamp) {
                        const partOfPeriod = (nowTimestamp - point[0]) / periodLength;
                        return total + point[1] * partOfPeriod / 1000;
                    } else {
                        return total;
                    }
                }, 0);
            };

            $scope.forecastLoading = false;
            $scope.hasSolarForecastData = false;
            $scope.hasConsumptionForecastData = false;

            let solarSeries = (data.records['solar_yield_forecast'] || []).map((point) => [point[0], point[1] / 1000]);

            let consumptionSeries = (data.records['vrm_consumption_fc'] || []).map((point) => [point[0], point[1] / 1000]);

            $scope.charts.liveFeed.loading = true;
            $scope.charts.liveFeed.series[11].visible = false;
            $scope.charts.liveFeed.series[11].data = [];
            $scope.charts.liveFeed.series[10].visible = false;
            $scope.charts.liveFeed.series[10].data = [];
            const [from, to] = nowIndicatorInterval()
            if (solarSeries.length > 0) {
                EssGraphService.addLiveFeedForecast($scope.charts.liveFeed, solarSeries, 10,
                    EssGraphService.colors.ORANGE, $scope.onlyForecast, from, to);
                $scope.historyTotals.solar_forecast_remaining = calcRemainingForecast(data.records['solar_yield_forecast']);
                $scope.hasSolarForecastData = true;
                $scope.isEntryActive["forecastSolar"] = $scope.legendSettings["forecastSolar"];
                $scope.charts.liveFeed.series[10].visible = $scope.isEntryActive["forecastSolar"];
            }

            if (consumptionSeries.length > 0) {
                EssGraphService.addLiveFeedForecast($scope.charts.liveFeed, consumptionSeries, 11,
                    EssGraphService.colors.RED, $scope.onlyForecast, from, to);
                $scope.historyTotals.consumption_forecast_remaining = calcRemainingForecast(data.records['vrm_consumption_fc']);
                $scope.hasConsumptionForecastData = true;
                $scope.isEntryActive["forecastConsumption"] = $scope.legendSettings["forecastConsumption"];
                $scope.charts.liveFeed.series[11].visible = $scope.isEntryActive["forecastConsumption"];
            }

            if ($scope.hasSolarForecastData || $scope.hasConsumptionForecastData) {
                $scope.forecastShown = true;
                $scope.allowForecast = true;
                if ($scope.onlyForecast) {
                    $scope.resetNowInidcator();
                }
            } else {
                // No forecast available, so not allowed
                $scope.allowForecast = false;
                $scope.forecastShown = false;
                $scope.showForecast = false;

                $scope.resetNowInidcator();
            }

            $scope.charts.liveFeed.loading = false;
            $scope.showGridHistory = !_.isEmpty($scope.historyTotals);
        }

        function disableForecasts(forceReload = false) {
            $scope.forecastShown = false;
            $scope.showForecastButton = true;
            $scope.charts.liveFeed.series[10].visible = false;
            $scope.charts.liveFeed.series[11].visible = false;
            delete $scope.isEntryActive['forecastSolar'];
            delete $scope.isEntryActive['forecastConsumption'];
            $scope.charts.liveFeed.series[12].visible = false;
            // reload the liveFeed graph
            if (forceReload)
                loadHistoryData(true);
        }

        function updateDynamicEssGraphs(withForecast = false) {
            if (!$scope.dynamicEssIsOn || !$scope.dessDatePicker.range.startDate || !$scope.dessDatePicker.range.endDate) return;

            let startDate = $scope.dessDatePicker.range.startDate;
            let endDate = $scope.dessDatePicker.range.endDate;
            let interval = 'hours'
            Object.keys($scope.dynamicEssCharts).forEach(key => {
                $scope.dynamicEssCharts[key].loading = true;
            });
            GraphService.getGraphData($stateParams.id, startDate, endDate, interval, 'stats', { type: 'dynamic_ess' })
                .then((data) => {
                    $scope.showForcedFullChargeHappenedWarning = data.totals && +data.totals.vrm_ffcs !== 0;
                    const [from, to] = nowIndicatorInterval('hours', false);
                    const [dailyTotalEarnings, dailyTotalGridEarnings, dailyTotalBatteryCosts, dailyTotalGridCosts] =
                        EssGraphService.addEssData(
                            $scope.charts,
                            data,
                            withForecast,
                            startDate,
                            endDate,
                            currentHour(),
                            from,
                            to,
                            $scope.installation.currency
                        );

                    $scope.dailyTotalEarnings = dailyTotalEarnings.toFixed(2);
                    $scope.dailyTotalGridEarnings = dailyTotalGridEarnings.toFixed(2);
                    $scope.dailyTotalBatteryCosts = Math.abs(dailyTotalBatteryCosts.toFixed(2));//The costs should be positive
                    $scope.dailyTotalGridCosts = Math.abs(dailyTotalGridCosts.toFixed(2));

                })
                .catch(e => {
                    vrmNotificationsService.add({
                        type: vrmNotificationType.ERROR,
                        message: locale.getString('dashboard.error_retrieving_dess_data')
                    });
                    throw new Error('error_retrieving_dess_prices: ' + e.message);
                })
                .finally(() => Object.keys($scope.dynamicEssCharts).forEach(key => {
                    $scope.dynamicEssCharts[key].loading = false;
                }))

            // Setting it to the smallest interval (15 mins) will make influx return its smallest available interval
            // for this site that is divisible by 15mins (i.e. 30mins, 60mins), etc.
            const pricesInterval = '15mins';
            Object.keys($scope.dynamicEssPricesCharts).forEach(key => {
                $scope.dynamicEssPricesCharts[key].loading = true;
            });
            GraphService.getGraphData($stateParams.id, startDate, endDate, pricesInterval, 'stats', { type: 'dynamic_ess_prices' })
                .then((data) => {
                    const [from, to] = nowIndicatorInterval('hours', false);
                    EssGraphService.addEssPricesData(
                        $scope.charts,
                        data,
                        withForecast,
                        startDate,
                        endDate,
                        currentHour(),
                        from,
                        to,
                        $scope.installation.currency
                    );
                })
                .catch(e => {
                    vrmNotificationsService.add({
                        type: vrmNotificationType.ERROR,
                        message: locale.getString('dashboard.error_retrieving_dess_prices')
                    });
                    throw new Error('error_retrieving_dess_prices_15mins: ' + e.message);
                })
                .finally(() => Object.keys($scope.dynamicEssPricesCharts).forEach(key => {
                    $scope.dynamicEssPricesCharts[key].loading = false;
                }))
        }

        function getIntervalDates() {
            const startDate = $scope.datePicker.range.startDate;
            const endDate = $scope.datePicker.range.endDate;
            return [startDate, endDate]
        }

        $scope.merge = function (...objects) {
            return Object.assign(...objects);
        }

        /**
         * Returns an object to be used in ng-class in flow-elements.
         * @param property Property to check value. Flow if value != 0, reverse flow if value < 0
         * @returns {{"flow-reverse": (*|boolean), flow: (*|boolean)}}
         */
        $scope.flow = function (property) {
            const value = $scope.isPropertyValid(property) ? Number($scope.ci[property].rawValue) : 0;
            return {
                'flow': Math.abs(value) > FLOW_THRESHOLD,
                'flow-reverse': value < 0
            };
        };

        $scope.flowMany = function (positives, negatives) {
            const getValue = (code) => {
                if (!$scope.isPropertyValid(code)) return 0;
                const value = Number($scope.ci[code].rawValue);
                return Math.abs(value) > FLOW_THRESHOLD ? value : 0;
            };
            const sumAttributes = (sum, code) => sum + getValue(code);
            const positivesSum = (positives || []).reduce(sumAttributes, 0);
            const negativesSum = (negatives || []).reduce(sumAttributes, 0);
            const sum = positivesSum - negativesSum;
            return {
                'flow': sum !== 0,
                'flow-reverse': sum < 0,
            };
        };

        $scope.toggleDetails = function () {
            $scope.showDetails = !$scope.showDetails;
            localStorageService.set(SHOW_DETAILS_STORAGEKEY, $scope.showDetails);
            $scope.positionLines();
            $scope.$broadcast('$$rebind::showDetails');

            if (!vrmConfig.debug && window.dataLayer) {
                const event = $scope.showDetails ? 'show' : 'hide';
                $log.info("GTM :: Logging event: " + event);
                window.dataLayer.push({
                    'eCategory': 'dashboard',
                    'eAction': 'details',
                    'eLabel': event,
                    'event': 'mInteraction'
                });
            }
        }

        $scope.toggleShowAll = function () {
            $scope.showAll = !$scope.showAll;
        }

        $scope.toggleEvChargerShowAll = function (type) {
            $scope.evChargerShowAll[type] = !$scope.evChargerShowAll[type];
            setTimeout($scope.positionLines, 20);
        }

        $scope.graphs.current = $scope.graphs.choices[0];


        $scope.generatorRuntimeProgress = {
            today: null,
            week: null,
            month: null,
            year: null
        };

        $scope.charts = {
            liveFeed: liveFeedGraph(),
            consumption: consumptionGraph(),
            solar: solarGraph(),
            generator: generatorGraph(),
            grid: gridGraph(),
            evcs: evcsGraph(),
            costsSavings: costsSavingsGraph(),
            essAlgorithm: essAlgorithmGraph(),
            energyPricesSchedule: energyPricesGraph(),
            energyPricesEnergy: energyPricesGraph(),
            usage: usageGraph(),
            environmental: environmentalGraph(),
            consumptionPies: {
                today: consumptionPieGraph(),
                week: consumptionPieGraph(),
                month: consumptionPieGraph(),
                year: consumptionPieGraph(),
            },
            solarPies: {
                today: solarPieGraph(),
                week: solarPieGraph(),
                month: solarPieGraph(),
                year: solarPieGraph(),
            },
            generatorPies: {
                today: generatorPieGraph(),
                week: generatorPieGraph(),
                month: generatorPieGraph(),
                year: generatorPieGraph(),
            },
            gridPies: {
                today: gridPieGraph(),
                week: gridPieGraph(),
                month: gridPieGraph(),
                year: gridPieGraph(),
            },
            current_limit: dashboardCurrentLimitGraph(),
            dashboard: {
                battery: dashboardBatteryGraph(),
                generator: dashboardGeneratorGraph(),
                pv_inverter_in: dashboardSolarGraph(),
                pv_inverter_out: dashboardSolarGraph(),
                pv_inverter_hub: dashboardSolarGraph(),
                pv_charger: dashboardSolarGraph(),
                dc_power: dashboardDcPowerGraph(),
                ac_loads: dashboardAcLoadsGraph(),
                solar: dashboardSolarGraph(),
                consumption: dashboardConsumptionGraph(),
                consumption_input: dashboardAcLoadsGraph(),
                consumption_output: dashboardConsumptionOutputGraph(),
                ev_power_in: dashboardEvPowerGraph(),
                ev_power_out: dashboardEvPowerGraph(),
                from_to_grid: dashboardFromToGridGraph(),
            },
        };
        $scope.dynamicEssCharts = [
            $scope.charts.costsSavings,
            $scope.charts.usage,
            $scope.charts.essAlgorithm
        ];
        $scope.dynamicEssPricesCharts = [
            $scope.charts.energyPricesSchedule,
            $scope.charts.energyPricesEnergy
        ];
        // Hide loading from the venus ui graphs, because the chart is so tiny the 'loading' label does not have enough space
        Object.keys($scope.charts.dashboard).forEach(key => {
            $scope.charts.dashboard[key].loading = false;
        });

        let pieTitles = {
            today: locale.getString('dashboard.last_day'),
            week: locale.getString('dashboard.last_week'),
            month: locale.getString('dashboard.last_month'),
            year: locale.getString('dashboard.last_year')
        };
        for (let period in pieTitles) {
            $scope.charts.consumptionPies[period].options.title.text = pieTitles[period];
            $scope.charts.solarPies[period].options.title.text = pieTitles[period];
            $scope.charts.generatorPies[period].options.title.text = pieTitles[period];
            $scope.charts.gridPies[period].options.title.text = pieTitles[period];
        }

        //Convert null dataset to empty dataset
        function emptyOnNull(data) {
            data = data ? data : [];
            // Highcharts requires sorted X values
            data = _.sortBy(data, function (data) {
                return data[0];
            });
            return data;
        }

        // data has to be sorted
        function putGaps(data, tick, interval) {
            const keys = data.map(i => i[0]);
            const dateAsKey = Object.fromEntries(data);
            for (let i = 0; i < keys.length - 1; i++) {
                if (interval != "15mins" && Math.round(moment(keys[i + 1]).diff(moment(keys[i]), interval, true)) > 1) {
                    dateAsKey[keys[i] + tick] = null;
                }
                if (interval == "15mins" && Math.round(moment(keys[i + 1]).diff(moment(keys[i]), "minutes", true)) > 15) {
                    dateAsKey[keys[i] + tick] = null;
                }
            }
            return emptyOnNull(Object.entries(dateAsKey).map(entry => [+entry[0], entry[1]]));
        }

        function getRangeData(data) {
            var result = [];
            _.each(data, function (record) {
                result.push([record[0], record[2], record[3]]);
            });
            return result;
        }

        var lastHash = null;
        var historyDataHash = null;

        $scope.chartHovers = {};
        $scope.showBlockDetails = localStorageService.get(
            SHOW_BLOCK_DETAILS_STORAGEKEY.replace('*', $scope.installation.id)
        ) || { temp: [] };

        $scope.toggleBlockDetailsVisibility = function (type, key) {
            if ($scope.showBlockDetails[type].includes(key)) {
                $scope.showBlockDetails[type] = $scope.showBlockDetails[type].filter(k => k !== key);
            } else {
                $scope.showBlockDetails[type].push(key);
            }
            localStorageService.set(SHOW_BLOCK_DETAILS_STORAGEKEY.replace('*', $scope.installation.id), $scope.showBlockDetails);
        }

        $scope.isBlockDetailsVisible = function (type, key) {
            return $scope.showBlockDetails[type].includes(key);
        }

        $scope.chartHover = function (name) {
            return $scope.chartHovers[name];
        }

        function getHistoryTotal(value, shouldRound = false) {
            if (value === null) {
                return null;
            }

            if (shouldRound && Math.abs(value) >= 1) {
                value = Math.round(value);
                return getFormattedValue({ formatWithUnit: GRID_HISTORY_FORMAT }, value);
            }

            if (value < 100) {
                return getFormattedValue({ formatWithUnit: GRID_HISTORY_FORMAT_ACCURATE }, value);
            }
            return getFormattedValue({ formatWithUnit: GRID_HISTORY_FORMAT }, value);
        }

        $scope.forceFormatHistory = getHistoryTotal;

        function configureChartHover(name, valueFormat) {
            // Setup initial hover value
            $scope.chartHovers[name] = null;
            // Create pre-bound value formatter
            const valueFormatter = sprintf.bind(undefined, valueFormat);
            // Highcharts options to merge
            const options = {
                plotOptions: {
                    series: {
                        point: {
                            events: {
                                mouseOver: function () {
                                    // We are running code outside of digest, so we need to wrap with $apply
                                    $scope.$apply(() => {
                                        const label = moment.utc(this.x).utcOffset($scope.installation.timezone_offset_textual).format('MMM DD, HH:mm');
                                        const value = valueFormatter(this.y);
                                        $scope.chartHovers[name] = { label, value };
                                    });
                                },
                                mouseOut: function () {
                                    $scope.$apply(() => {
                                        $scope.chartHovers[name] = null;
                                    });
                                },
                            },
                        },
                    },
                },
                tooltip: { enabled: false },
            };
            // Perform merge
            $scope.charts.dashboard[name] = _.merge($scope.charts.dashboard[name], { options });
        }

        configureChartHover('battery', UNITS2FORMATTING.SOC);
        configureChartHover('generator', UNITS2FORMATTING.W);
        configureChartHover('pv_inverter_in', UNITS2FORMATTING.W);
        configureChartHover('pv_inverter_out', UNITS2FORMATTING.W);
        configureChartHover('pv_inverter_hub', UNITS2FORMATTING.W);
        configureChartHover('pv_charger', UNITS2FORMATTING.W);
        configureChartHover('dc_power', UNITS2FORMATTING.W);
        configureChartHover('solar', UNITS2FORMATTING.W);
        configureChartHover('consumption', UNITS2FORMATTING.W);
        configureChartHover('from_to_grid', UNITS2FORMATTING.W);
        configureChartHover('ac_loads', UNITS2FORMATTING.W);
        configureChartHover('consumption_input', UNITS2FORMATTING.W);
        configureChartHover('consumption_output', UNITS2FORMATTING.W);
        configureChartHover('ev_power_in', UNITS2FORMATTING.W);
        configureChartHover('ev_power_out', UNITS2FORMATTING.W);

        function getBarHeight(current, previous) {
            return current < 0 ? Math.min(current, previous) : Math.max(current, previous);
        }

        function siteNow() {
            let date = new Date()
            return Math.floor((date.getTime()) / 1000);
        }
        function roundDate(timestamp) {
            return Math.floor(timestamp / 3600) * 3600
        }

        function isIdle(timestamp) {
            return ((new Date() / 1000) - timestamp) > 3600;
        }

        function getLogInterval() {
            const seconds = $scope.isPropertyValid('sl') ? +$scope.ci.sl.rawValue : 0;
            const quarterHour = 15 * 60;
            const hour = quarterHour * 4;
            const day = 24 * hour;
            const week = 7 * day;
            const month = (365.25 * day) / 12;
            if (seconds <= quarterHour) return '15mins';
            if (seconds <= hour) return 'hours';
            if (seconds <= (2 * hour)) return '2hours';
            if (seconds <= day) return 'days';
            if (seconds <= week) return 'weeks';
            if (seconds <= month) return 'months';
            return 'years';
        }

        function loadMinMaxTemperatures() {
            GraphService.getGraphData(
                $stateParams.id,
                roundDate(siteNow() - 24 * 60 * 60),
                siteNow(),
                getLogInterval(),
                "stats?attributeCodes[]=tsT",
                { type: "custom", show_instance: true }
            ).then(function ({ records }) {
                if (!records || !records.length) {
                    return;
                }
                const calculatedMinMax = records.reduce(
                    (rx, { instance, stats }) => {
                        const { tsT } = stats;

                        if (!tsT || !tsT.length) {
                            return rx;
                        }

                        rx[instance] = calculateMinMaxAvg(tsT);
                        return rx;
                    }, {}
                );

                $scope.temperaturesMinMaxPerDay = calculatedMinMax;
            });
        }

        function calculateMinMaxAvg(entries) {
            const { min, max } = entries.reduce(
                // eslint-disable-next-line no-unused-vars
                (rx, [_interval, _avg, min, max]) => {
                    rx.min = Math.min(min, rx.min);
                    rx.max = Math.max(max, rx.max);
                    return rx;
                },
                { min: Number.POSITIVE_INFINITY, max: Number.NEGATIVE_INFINITY }
            );
            if (!$scope.ci.tsT || !$scope.ci.tsT.formatWithUnit) {
                return { min, max };
            }
            const { formatWithUnit } = $scope.ci.tsT;
            const format = formatWithUnit.replace('F', 'f');
            const minFormatted = sprintf(format, min);
            const maxFormatted = sprintf(format, max);
            return { min: minFormatted, max: maxFormatted };
        }

        function loadVenusData() {

            GraphService.getGraphData($stateParams.id, roundDate(siteNow() - (24 * 60 * 60)), siteNow(), getLogInterval(), 'stats', { type: 'venus' }).then(function (data) {

                $scope.charts.dashboard.from_to_grid.series[0].data = emptyOnNull(data.records.from_to_grid)
                $scope.charts.dashboard.battery.series[0].data = emptyOnNull(data.records.bs);
                $scope.charts.dashboard.pv_inverter_in.series[0].data = emptyOnNull(data.records.ac_in);
                $scope.charts.dashboard.pv_inverter_out.series[0].data = emptyOnNull(data.records.ac_out);
                $scope.charts.dashboard.pv_inverter_hub.series[0].data = emptyOnNull(data.records[$scope.hubInverterSource]);
                $scope.charts.dashboard.pv_charger.series[0].data = emptyOnNull(data.records.Pdc);
                $scope.charts.dashboard.dc_power.series[0].data = emptyOnNull(data.records.dc);
                $scope.charts.dashboard.ac_loads.series[0].data = emptyOnNull(data.records.ac_loads);
                $scope.charts.dashboard.solar.series[0].data = emptyOnNull(data.records.solar_yield);
                $scope.charts.dashboard.consumption.series[0].data = emptyOnNull(data.records.consumption);
                $scope.charts.dashboard.consumption_input.series[0].data = emptyOnNull(data.records.consumption_input);
                $scope.charts.dashboard.consumption_output.series[0].data = emptyOnNull(data.records.consumption_output);
                $scope.charts.dashboard.ev_power_in.series[0].data = emptyOnNull(data.records.evp_in);
                $scope.charts.dashboard.ev_power_out.series[0].data = emptyOnNull(data.records.evp_out);


                $scope.totalSolarYield = getHistoryTotal(data.totals.total_solar_yield);
            }).catch(function (error) {
                Object.keys($scope.charts.dashboard).forEach(key => {
                    $scope.charts.dashboard[key].series[0].data = [];
                });
                throw error;
            }).finally(function () {
                // Due to concurrency problems, we have to make sure the zones are defined after the data is loaded,
                // otherwise the color of the negative values might be wrong.

                $timeout($scope.charts.dashboard.from_to_grid.options.plotOptions.area.zones.push({
                    value: 0,
                    color: GraphColors.lightGreen,
                }), 100);

            });
        }

        function updateCharts() {
            const charts = $scope.charts.dashboard;
            const now = Date.now();
            const installation = $scope.ci;

            // Drops entries that are older than 24 hours
            function dropOutdatedEntries(entries) {
                const threshold = now - 24 * 60 * 60 * 1000;
                // Find the index of the first item that exits after the threshold
                const firstFreshIndex = entries.findIndex(entry => {
                    const timestamp = entry[0];
                    return threshold >= timestamp;
                });
                // Drop all entries before the threshold
                if (firstFreshIndex >= 0) {
                    entries.splice(0, firstFreshIndex);
                }
            }

            // Drop all outdated entries on the primary series
            Object.keys(charts).forEach(key => {
                dropOutdatedEntries(charts[key].series[0].data);
            });

            // Helper function to add data if there is sufficient historical data
            const addDataIfValid = (chart, value) => {
                const validData = emptyOnNull([[now, parseFloat(value)]]);
                if (validData.length > 0 && chart.series[0].data.length > 0) {
                    chart.series[0].data.push(validData[0]);
                }
            };

            // Simple updates with valid data check
            addDataIfValid(charts.battery, installation.bs.rawValue);
            addDataIfValid(charts.pv_inverter_in, installation.ac_in.rawValue);
            addDataIfValid(charts.pv_inverter_out, installation.ac_out.rawValue);
            addDataIfValid(charts.pv_inverter_hub, installation[$scope.hubInverterSource].rawValue);
            addDataIfValid(charts.pv_charger, installation.Pdc.rawValue);
            addDataIfValid(charts.dc_power, installation.dc.rawValue);
            addDataIfValid(charts.ac_loads, installation.ac_loads.rawValue);
            addDataIfValid(charts.solar, installation.solar_yield.rawValue);
            addDataIfValid(charts.consumption, installation.consumption.rawValue);
            addDataIfValid(charts.consumption_input, installation.consumption_input.rawValue);
            addDataIfValid(charts.consumption_output, installation.consumption_output.rawValue);
            addDataIfValid(charts.from_to_grid, installation.from_to_grid.rawValue);
            if (installation.ev_car_charger_in) {
                addDataIfValid(charts.ev_power_in, installation.ev_car_charger_in.rawValue);
            }
            if (installation.ev_car_charger_out) {
                addDataIfValid(charts.ev_power_out, installation.ev_car_charger_out.rawValue);
            }
        }

        function hasSupposedControls() {
            const relay0Function = $scope.ci.rf0 ? $scope.ci.rf0.rawValue : -1;
            const relay1Function = $scope.ci.rf1 ? $scope.ci.rf1.rawValue : -1;

            return hasInstances($scope, 'eve')  // evcs
                || (hasInstances($scope, 'H4bs') && $scope.isEssTypeSystem) // ess
                || hasInstances($scope, 'gaA') // generator start/stop
                || (hasInstances($scope, 'cRelay') && relay0Function == 2)  // relay 0
                || (hasInstances($scope, 'cRelay1') && relay1Function == 2)  // relay 1
                || hasInstances($scope, 's') // inverter/charger
        }

        function updateCapabilityFlags() {
            if (!$scope.ci.cp || !$scope.ci.cp.rawValue) {
                $scope.hasNewGeneratorPaths = false;
                $scope.hasDynamicEssCapability = false;
                return;
            }
            const rawValue = +$scope.ci.cp.rawValue;
            $scope.hasNewGeneratorPaths = !!(rawValue & (1 << 4)) && hasInstances($scope, 'gaA');
            $scope.hasDynamicEssCapability = !!(rawValue & (1 << 6));

            // Check if dess is on and whether the dess dashboard should be shown if this user has right permissions
            if ($scope.hasDynamicEssCapability && $scope.dynamicEssIsOn === null && $scope.dynamicEssAvailable) {
                ApiService.call2(
                    '/installations/' + $stateParams.id + '/dynamic-ess-settings',
                    {},
                    { method: 'GET', skipResultChecks: true },
                ).then(function (response) {
                    if (!response.data.success) {
                        $scope.hasDynamicEssConfiguration = false;
                        $scope.dynamicEssIsOn = false;
                        return;
                    }
                    $scope.hasDynamicEssConfiguration = response.data.data && Object.hasOwn(response.data.data, 'isOn') && response.data.data.buyPriceType !== null;
                    $scope.dynamicEssIsOn = $scope.hasDynamicEssConfiguration && response.data.data.isOn;
                    updateDynamicEssGraphs(true);
                });
            }
        }


        function updateEVChargingBlocks() {
            const { evp, evm, evcn, evt, evi, evf, evs, evl } = $scope.ci;

            let evcsInInstanceIds = [];
            $scope.hasEvCarChargerIn = false;
            // by default all instances are out instances
            // this way users that do not have evl can still see their evcs
            let evcsOutInstanceIds = (evp && evp.instances) ? Object.keys(evp.instances) : [];
            $scope.hasEvCarChargerOut = !!evcsOutInstanceIds.length;

            if ($scope.isPropertyValid('evl')) {
                const instances = evl.instances ? Object.entries(evl.instances) : [];
                if (instances.length != 0) {
                    evcsOutInstanceIds = instances.filter(([, instance]) => parseInt(instance.rawValue) === 0).map(([id]) => id)
                    evcsInInstanceIds = instances.filter(([, instance]) => [1, 2].includes(parseInt(instance.rawValue))).map(([id]) => id)
                    $scope.hasEvCarChargerIn = !!evcsInInstanceIds.length;
                    $scope.hasEvCarChargerOut = !!evcsOutInstanceIds.length;
                }
            }

            if (!$scope.hasEvCarChargerOut && !$scope.hasEvCarChargerIn) {
                return;
            }
            const getSum = (attribute, includedInstances) => {
                const { instances } = attribute;
                const sum = sumBy(Object.entries(instances),
                    function ([instanceId, instance]) {
                        if (!includedInstances.includes(instanceId)) {
                            return 0;
                        }
                        return +(instance.rawValue || 0)
                    });
                return sum;
            }
            const getInstance = (attribute, instance) => {
                if (!attribute) {
                    return null;
                }
                const { instances } = attribute;
                return instances && instances[instance] && instances[instance].formattedValue;
            }
            const getFormattedInstances = (includedInstances) => {
                let { instances } = evp;
                const formattedInstances = instances ? Object.keys(instances).map(key => {
                    if (!includedInstances.includes(key)) {
                        return null;
                    }

                    return {
                        power: getInstance(evp, key),
                        mode: getInstance(evm, key),
                        state: getInstance(evs, key),
                        time: getInstance(evt, key),
                        chargeCurrent: getInstance(evi, key),
                        totalEnergy: getInstance(evf, key),
                        name: getInstance(evcn, key) || ("EVCHARGER-" + key),
                    }
                }) : [];
                return sortBy(formattedInstances.filter(i => !!i), "name");
            }
            const buildEvChargerData = (instanceKeys) => {
                const evChargerData = {};
                // ["Connected", "Charging", "Charged"]
                const powerStates = ["1", "2", "3"]
                const atLeastOneIsConnected = evs.instances
                    && Object.entries(evs.instances)
                        .some(([instanceId, instance]) =>
                            instanceKeys.includes(instanceId) &&
                            powerStates.includes(String(instance.rawValue)));
                evChargerData.titleClass = "";
                if (atLeastOneIsConnected) {
                    evChargerData.main = getFormattedValue(evp, getSum(evp, instanceKeys));
                } else if (instanceKeys.length > 0 && evs.instances && evs.instances.hasOwnProperty(instanceKeys[0])) {
                    // none are connected, get the text status of the first one (if there are any)
                    evChargerData.main = evs.instances[instanceKeys[0]].textValue;
                    evChargerData.titleClass = "smaller-font"
                }
                const allInstances = getFormattedInstances(instanceKeys);
                const [instances = [], ...restInstances] = chunk(allInstances, 3);
                evChargerData.instances = instances;
                evChargerData.restInstances = (restInstances || []).flat();

                return evChargerData;
            }

            $scope.evChargerDataIn = buildEvChargerData(evcsInInstanceIds);
            $scope.evChargerDataOut = buildEvChargerData(evcsOutInstanceIds);

            // "virtualize" ci attributes
            // needed for flowMany and other ci utility functions
            $scope.ci.ev_car_charger_out = { rawValue: getSum(evp, evcsOutInstanceIds) }
            $scope.ci.ev_car_charger_in = { rawValue: getSum(evp, evcsInInstanceIds) }
        }

        function updateTemperatureBlocks() {
            const { tsT, tscn, tst, tsH, tsB } = $scope.ci;
            if (!tsT || !tsT.instances) {
                $scope.temperatures = [];
                return;
            }

            const temperatures = Object.entries(tsT.instances).map(
                ([key, instance]) => {
                    const customName = getValueFromAttr(tscn, key);
                    const temperatureType = getValueFromAttr(tst, key);
                    const temperatureTypeId = getSafeInstancesProp(tst, key, null);

                    const temperaturesWithSameType = tst.instances
                        ? Object.values(tst.instances).filter(
                            instance =>
                                instance.textValue ===
                                temperatureType ||
                                instance.formattedValue ===
                                temperatureType
                        ).length
                        : 0;
                    const typeInstance =
                        temperaturesWithSameType > 1
                            ? ` [${key}]`
                            : "";

                    const typeTranslationKey = getTemperatureSensorTypeTranslationKey(temperatureTypeId);
                    const typeName = temperatureType && typeTranslationKey ? (locale.getString(typeTranslationKey) + typeInstance) : null;

                    // show custom name if available, if not show the type (if the instance is the only type don't show the key in the name)
                    // if temperature type is generic and custom name is not available handle the name on the front
                    const temperatureName =
                        customName ||
                        (temperatureType !== "Generic"
                            ? typeName
                            : null);
                    return {
                        ...instance,
                        lastUpdated: instance.timestamp * 1000,
                        wasUpdatedInTheLast24h: moment().diff(moment.unix(instance.timestamp), 'days') === 0,
                        idle: isIdle(instance.timestamp),
                        instance: +key,
                        humidity: getValueFromAttr(tsH, key),
                        barometricPressure: getValueFromAttr(tsB, key),
                        temperatureName
                    };
                }
            );

            $scope.temperatures = _.sortBy(temperatures, ['instance'])
        }

        function updateTanks() {
            if (!$scope.ci.tf || !$scope.ci.tf.instances) {
                $scope.groupedTanks = [];
                return;
            }
            const tanks = Object.keys(
                $scope.ci.tf.instances
            ).reduce((rx, tankKey) => {
                const tankLevel =
                    $scope.ci.tl.instances &&
                    $scope.ci.tl.instances[tankKey];

                // if tank doesn't have a tanklevel, skip it
                if (!tankLevel) {
                    return rx;
                }

                const tankType = getSafeInstancesProp($scope.ci.tf, tankKey, null);
                let tankTypeName = null;
                if (tankType || tankType === 0) {
                    tankTypeName = locale.getString(getTankTypeTranslationKey(tankType));
                }

                return {
                    ...rx,
                    [tankKey]: {
                        lastUpdated: tankLevel.timestamp * 1000,
                        idle: isIdle(tankLevel.timestamp),
                        tankFluid: $scope.ci.tf.instances[tankKey],
                        tankLevel,
                        customName:
                            $scope.ci.tcn.instances &&
                            $scope.ci.tcn.instances[tankKey],
                        typeName: tankTypeName,
                        instance: +tankKey
                    }
                };
            }, {});
            const groupedTanks = groupBy(tanks, (item) => item.tankFluid.textValue);
            const orderedGroups = _(groupedTanks).toPairs().sortBy(0).fromPairs().value();
            $scope.groupedTanks = orderedGroups;
        }

        $scope.$watch('installation', function () {
            // Update the range so we keep it 'realtime' and shift the range over
            if (!isZoomedIn($scope.charts[$scope.graphs.current.id])) {
                // Set end date to now which updates the pickers and triggers a data reload.
                $scope.datePicker.control.refreshRange();
                if (!$scope.datePicker.range.endsNow && $scope.datePicker.range.keepRealtime) {
                    dateChangeObserver();
                }
            }
        });

        $scope.$watchGroup(['isAcGenerator', 'hasGeneratorKwhData'], function () {
            $scope.graphs.choices[3].available = ($scope.isAcGenerator || $scope.hasGeneratorKwhData) ? 'yes' : 'no';
        },);

        $scope.$watch(function () {
            return {
                isAcGrid: $scope.isAcGrid,
                hasAcGridKwh: $scope.hasAcGridKwh,
            }
        }, function (newVal) {
            $scope.graphs.choices[4].available = (newVal.isAcGrid || newVal.hasAcGridKwh) ? 'yes' : 'no';
        }, true);

        let requestCounter = 0;

        function dateChangeObserver() {

            $scope.showForecastButton = false;
            if ($scope.graphs.current.id === 'liveFeed' &&
                isDateRangeForecastAllowed() &&
                checkCanHaveForecast()) {
                $scope.allowForecast = true;
                $scope.showForecast = getForecast();
            } else {
                $scope.allowForecast = false;
                $scope.toggleForecast(false, false, false);
            }

            if ($scope.datePicker.range.name &&
                $scope.onlyForecastQuickranges.includes($scope.datePicker.range.name) &&
                $scope.graphs.current.id === 'liveFeed') {
                $scope.onlyForecast = true;
                $scope.showForecast = true;
                $scope.mustHaveForecast = false;
            } else if ($scope.datePicker.range.name &&
                $scope.mustHaveForecastQuickranges.includes($scope.datePicker.range.name) &&
                $scope.graphs.current.id === 'liveFeed') {
                $scope.onlyForecast = false;
                $scope.showForecast = true;
                $scope.mustHaveForecast = true;
            } else {
                $scope.onlyForecast = false;
                $scope.mustHaveForecast = false;
            }

            const keepRealtimeStamp = !$scope.datePicker.range.endsNow && $scope.datePicker.range.keepRealtime ? Math.floor(+new Date() / 1000) : false;
            let currentHash = [$scope.datePicker.range.startDate, $scope.datePicker.range.endDate, $scope.graphs.current.id, keepRealtimeStamp].join();
            if (historyDataHash !== currentHash || historyDataHash === null) {
                const loaded = loadHistoryData();
                if (loaded) {
                    historyDataHash = currentHash;
                } else {
                    historyDataHash = null;
                }
            }
        }

        function dessDateChangeObserver() {
            updateDynamicEssGraphs(true);
        }

        function graphDataInFuture() {
            if ($scope.datePicker.range.name &&
                $scope.onlyForecastQuickranges.includes($scope.datePicker.range.name))
                return true;
            return $scope.datePicker.range.startDate > siteNow() - 60;
        }



        function loadHistoryData(forceReload = true) {
            let [startDate, endDate] = getIntervalDates()

            if (!$scope.installation || !startDate) {
                return false;
            }

            var precision = $scope.datePicker.range.precision;
            var interval = $scope.datePicker.range.interval;
            var validUntil = $scope.datePicker.range.reloadEndDate;
            var dataURL = $scope.graphs.current.dataURL;
            var type = $scope.graphs.current.type;
            var showInstance = $scope.graphs.current.show_instance;

            if ($scope.datePicker.range.name) {
                startDate = $scope.datePicker.range.roundedStartDate;
            }

            // Collect all parameters, the null ones are not url encoded.
            var parameters = {
                type: type,
                show_instance: showInstance
            };

            // Skip data load if it is not necessary
            var currentHash = $stateParams.id + '_' + startDate + '_' + validUntil + '_' + parameters.type + '_' + interval;
            if (!forceReload && currentHash === lastHash) return;

            if ($scope.graphs.current) {
                $scope.charts[$scope.graphs.current.id].loading = true;
                // Clear the zoom state.
                resetZoomState($scope.charts[$scope.graphs.current.id]);
            }

            const currentRequest = ++requestCounter;

            if (graphDataInFuture()) {
                $scope.charts.isLoading = true;
                // Disable all history data series
                for (let chart in $scope.charts) {
                    if ($scope.charts[chart].series && !$scope.dynamicEssCharts.includes($scope.charts[chart]) && !$scope.dynamicEssPricesCharts.includes($scope.charts[chart])) {
                        for (let s = 0; s < $scope.charts[chart].series.length; s++) {
                            if (chart === 'current_limit') {
                                // Reset current_limit data sets to the default to prevent assigning data to undefined
                                // properties.
                                $scope.charts[chart].series[s].data = dashboardCurrentLimitGraph().series[s].data;
                            } else {
                                $scope.charts[chart].series[s].data = [];
                            }
                        }
                        $scope.charts[chart].loading = false;
                    }
                }

                $scope.toggleForecast($scope.showForecast, forceReload, false);
                $scope.charts.liveFeed.xAxis.tickInterval = precision * 1000;
                $scope.charts.liveFeed.xAxis.softMin = startDate * 1000;
                $scope.charts.liveFeed.xAxis.softMax = (endDate - precision) * 1000;
                updateLegend();
                return false;
            }

            GraphService.getGraphData($stateParams.id, startDate, endDate, interval, dataURL, parameters).then(function (data) {
                // Cancel if the user has updated the UI before the request completed, avoids race conditions
                if (requestCounter !== currentRequest) return;
                const { records, totals } = data;
                var dataFound = false;
                var maxLines = 0;

                $scope.historyTotals = {};

                lastHash = currentHash;
                const gapTick = precision * 1000;

                // Assign liveFeed chart data
                if ($scope.graphs.current.id === 'liveFeed') {
                    // Set kwh data
                    $scope.charts.liveFeed.series[0].data = putGaps(emptyOnNull(records.total_consumption), gapTick, interval);
                    $scope.charts.liveFeed.series[1].data = putGaps(emptyOnNull(records.total_solar_yield), gapTick, interval);

                    // flag if dataset contains kwh data
                    $scope.hasKwhData = $scope.charts.liveFeed.series[0].data.length > 0 || $scope.charts.liveFeed.series[1].data.length > 0;
                    $scope.hasConsumptionKwhData = $scope.charts.liveFeed.series[0].data.length > 0;
                    $scope.hasSolarKwhData = $scope.charts.liveFeed.series[1].data.length > 0;

                    $scope.charts.liveFeed.series[9].data = putGaps(emptyOnNull(records.total_genset), gapTick, interval);
                    $scope.hasGeneratorKwhData = $scope.charts.liveFeed.series[9].data.length > 0;

                    $scope.charts.liveFeed.series[2].data = putGaps(emptyOnNull(records.bs), gapTick, interval);
                    $scope.charts.liveFeed.series[3].data = getRangeData(emptyOnNull(records.bs));
                    $scope.hasSoc = $scope.charts.liveFeed.series[2].data.length > 0;
                    $scope.hasBatteryData = $scope.hasSoc || $scope.charts.liveFeed.series[3].data.length > 0;

                    if ($scope.hasSoc && $scope.isPropertyValid('bs')) {
                        const timestamp = $scope.charts.liveFeed.series[2].data[$scope.charts.liveFeed.series[2].data.length - 1][0];
                        const newValue = Number($scope.ci.bs.rawValue);

                        if ($scope.datePicker.range.endsNow || $scope.datePicker.range.keepRealtime) {
                            $scope.charts.liveFeed.series[8].data = [[timestamp, newValue], [timestamp, newValue]];
                        } else {
                            $scope.charts.liveFeed.series[8].data = []
                        }
                    }

                    $scope.charts.liveFeed.series[4].data = putGaps(emptyOnNull(records.Pdc), gapTick, interval);
                    $scope.hasPdcData = $scope.charts.liveFeed.series[4].data.length > 0;

                    $scope.charts.liveFeed.series[5].data = putGaps(emptyOnNull(records.iOI1), gapTick, interval);
                    $scope.hasiOI1Data = $scope.charts.liveFeed.series[5].data.length > 0;

                    $scope.charts.liveFeed.series[6].data = putGaps(emptyOnNull(records.bv), gapTick, interval);
                    $scope.charts.liveFeed.series[7].data = getRangeData(emptyOnNull(records.bv));
                    $scope.hasBvData = $scope.charts.liveFeed.series[6].data.length > 0;
                    // Detect when not to show the solar tab
                    const Pt = $scope.isPropertyValid('Pt');
                    const Pc = $scope.isPropertyValid('Pc');
                    const Pb = $scope.isPropertyValid('Pb');
                    const Pg = $scope.isPropertyValid('Pg');
                    const boringSolar = Pt && (!Pc && !Pb && !Pg);

                    // Toggle availability of Consumption and Solar Yield
                    $scope.graphs.choices[1].available = $scope.hasConsumptionKwhData ? 'yes' : 'no';
                    $scope.graphs.choices[2].available = $scope.hasSolarKwhData && !boringSolar ? 'yes' : 'no';

                    const {
                        grid_history_from,
                        grid_history_to,
                        total_solar_yield,
                        total_consumption,
                        total_genset,
                    } = totals;

                    if (total_genset) {
                        $scope.historyTotals.generator = total_genset;
                    }

                    if (grid_history_from || grid_history_to || total_consumption) {
                        $scope.historyTotals.fromGrid = getHistoryTotal(grid_history_from);
                        $scope.historyTotals.toGrid = getHistoryTotal(grid_history_to);
                        $scope.hasAcGridKwh = grid_history_to || grid_history_from;
                        $scope.historyTotals.totalConsumption = getHistoryTotal(total_consumption);
                    }
                    if (total_solar_yield) {
                        $scope.historyTotals.totalProduction = getHistoryTotal(total_solar_yield);
                    }

                    maxLines = 6;
                } else if ($scope.graphs.current.id === 'consumption') {
                    $scope.charts.consumption.series[0].data = putGaps(emptyOnNull(records.Gc), gapTick, interval); // Grid
                    $scope.charts.consumption.series[1].data = putGaps(emptyOnNull(records.gc), gapTick, interval); // Genset
                    $scope.charts.consumption.series[2].data = putGaps(emptyOnNull(records.Bc), gapTick, interval); // Battery
                    $scope.charts.consumption.series[3].data = putGaps(emptyOnNull(records.Pc), gapTick, interval); // Solar

                    const {
                        Gc,
                        gc,
                        Bc,
                        Pc,
                    } = totals;

                    if (Gc || gc || Bc || Pc) {
                        $scope.historyTotals.fromGrid = getHistoryTotal(Gc);
                        $scope.historyTotals.fromGenset = getHistoryTotal(gc);
                        $scope.historyTotals.fromBattery = getHistoryTotal(Bc);
                        $scope.historyTotals.fromSolar = getHistoryTotal(Pc);
                        $scope.historyTotals.totalConsumption = getHistoryTotal(Gc + gc + Bc + Pc);
                    }

                    // Right axis logic
                    // bs (SOC)
                    if ($scope.hasSoc) {
                        $scope.charts.liveFeed.yAxis[1].visible = true;
                        $scope.charts.liveFeed.series[2].visible = true;
                        $scope.charts.liveFeed.series[3].visible = true;
                    }
                    maxLines = 3;
                } else if ($scope.graphs.current.id === 'solar') {
                    $scope.charts.solar.series[0].data = putGaps(emptyOnNull(records.Pg), gapTick, interval); // To grid
                    $scope.charts.solar.series[1].data = putGaps(emptyOnNull(records.Pb), gapTick, interval); // To battery
                    $scope.charts.solar.series[2].data = putGaps(emptyOnNull(records.Pc), gapTick, interval); // To direct use
                    $scope.charts.solar.series[3].data = putGaps(emptyOnNull(records.Pt), gapTick, interval); // To unknown, general attribute

                    const {
                        Pg,
                        Pb,
                        Pc,
                        Pt,
                    } = totals;

                    if (Pg || Pb || Pc || Pt) {
                        $scope.historyTotals.toGrid = getHistoryTotal(Pg);
                        $scope.historyTotals.toBattery = getHistoryTotal(Pb);
                        $scope.historyTotals.directUse = getHistoryTotal(Pc);
                        $scope.historyTotals.totalProduction = getHistoryTotal(Pg + Pb + Pc + Pt);
                    }
                    maxLines = 3;
                } else if ($scope.graphs.current.id === 'generator') {
                    $scope.charts.generator.series[0].data = putGaps(emptyOnNull(records.gb), gapTick, interval); // To battery
                    $scope.charts.generator.series[1].data = putGaps(emptyOnNull(records.gc), gapTick, interval); // To consumers
                    const {
                        gb,
                        gc,
                    } = totals;

                    if (gb || gc) {
                        $scope.historyTotals.toBattery = getHistoryTotal(gb);
                        $scope.historyTotals.directUse = getHistoryTotal(gc);
                        $scope.historyTotals.totalKwh = getHistoryTotal(gb + gc);
                    }

                    maxLines = 1;
                } else if ($scope.graphs.current.id === 'grid') {
                    $scope.charts.grid.series[0].data = putGaps(emptyOnNull(records.Gb), gapTick, interval); // To battery
                    $scope.charts.grid.series[1].data = putGaps(emptyOnNull(records.Gc), gapTick, interval); // To consumers

                    const {
                        Gb,
                        Gc,
                        Bg,
                        Pg,
                    } = totals;

                    if (Gb || Gc) {
                        $scope.historyTotals.toBattery = getHistoryTotal(Gb);
                        $scope.historyTotals.directUse = getHistoryTotal(Gc);
                        $scope.historyTotals.totalKwh = getHistoryTotal(Gb + Gc);
                    }

                    if (Bg) {
                        $scope.historyTotals.batteryToGrid = getHistoryTotal(Bg);
                    }

                    if (Pg) {
                        $scope.historyTotals.pvToGrid = getHistoryTotal(Pg);
                    }
                    maxLines = 1;
                } else if ($scope.graphs.current.id === 'evcs') {
                    const evcsInstanceIds = Object.keys($scope.ci.evE.instances).map(id => parseInt(id));
                    $scope.charts.evcs.series = $scope.charts.evcs.getSeriesByInstanceIds(evcsInstanceIds);
                    $scope.charts.evcs.series.map((s) => {
                        const data = records.find(r => r.instance === s.instance);
                        const evcn = $scope.ci.evcn.instances[s.instance];
                        const total = totals.find(t => t.instance === s.instance);
                        if (data) s.data = putGaps(emptyOnNull(data.stats.evE), gapTick, interval); // 15min energy delta
                        if (evcn) s.name = evcn.formattedValue;
                        s.total = getHistoryTotal(total ? total.totals.evE : 0);
                    });
                    maxLines = 1;
                }
                $scope.historyTotals.count = !_.isEmpty($scope.historyTotals) ? Object.keys($scope.historyTotals).length : 0;
                $scope.showGridHistory = !_.isEmpty($scope.historyTotals);

                for (var i = 0; i <= maxLines; i++)
                    dataFound = dataFound || $scope.charts[$scope.graphs.current.id].series[i].data.length > 0;
                // Show FAQ about no data if there is no data and the last connection of the site is greater than the selected
                // start date
                $scope.showFaqAboutCharts = !dataFound && (startDate < $scope.installation.last_timestamp);

                $scope.charts[$scope.graphs.current.id].xAxis.tickInterval = null;
                if (dataFound) {
                    $scope.charts[$scope.graphs.current.id].xAxis.softMin = startDate * 1000;
                    // Set tick interval to current precision setting (makes sure every day is a tick for example)
                    $scope.charts[$scope.graphs.current.id].xAxis.tickInterval = precision * 1000;
                    // make sure the tick interval is also doubled if a 2hour interval is used.
                    // if(interval === '2hours') {
                    //     $scope.charts[$scope.graphs.current.id].xAxis.tickInterval *= 2;
                    // }
                    // Set max of the x-axis to the correct value, remove 1 precision for closing data
                    $scope.charts[$scope.graphs.current.id].xAxis.softMax = (endDate - precision) * 1000;
                }
                // Cap tooltip ranges to the correct dates
                if (!$scope.datePicker.range.name) {
                    $scope.charts[$scope.graphs.current.id].options.tooltip.rangeCap = [startDate * 1000, endDate * 1000];
                } else {
                    $scope.charts[$scope.graphs.current.id].options.tooltip.rangeCap = null;
                }
            }).catch(function () {
                if ($scope.graphs.current.id === 'liveFeed') {
                    $scope.charts.liveFeed.series[0].data = [];
                    $scope.charts.liveFeed.series[1].data = [];
                    $scope.charts.liveFeed.series[2].data = [];
                    $scope.charts.liveFeed.series[3].data = [];
                    $scope.charts.liveFeed.series[4].data = [];
                    $scope.charts.liveFeed.series[5].data = [];
                    $scope.charts.liveFeed.series[6].data = [];
                    $scope.charts.liveFeed.series[7].data = [];
                    $scope.charts.liveFeed.series[8].data = [];
                    $scope.charts.liveFeed.series[9].data = [];
                } else if ($scope.graphs.current.id === 'consumption') {
                    $scope.charts.consumption.series[0].data = [];
                    $scope.charts.consumption.series[1].data = [];
                    $scope.charts.consumption.series[2].data = [];
                    $scope.charts.consumption.series[3].data = [];
                } else if ($scope.graphs.current.id === 'solar') {
                    $scope.charts.solar.series[0].data = [];
                    $scope.charts.solar.series[1].data = [];
                    $scope.charts.solar.series[2].data = [];
                    $scope.charts.solar.series[3].data = [];
                } else if ($scope.graphs.current.id === 'generator') {
                    $scope.charts.generator.series[0].data = [];
                    $scope.charts.generator.series[1].data = [];
                } else if ($scope.graphs.current.id === 'grid') {
                    $scope.charts.grid.series[0].data = [];
                    $scope.charts.grid.series[1].data = [];
                } else if ($scope.graphs.current.id === 'evcs') {
                    $scope.charts.evcs.series.map(s => s.data = []);
                }
            }).then(function () {
                if ($scope.graphs.current.id === 'liveFeed') {
                    for (var i = 0; i <= 9; i++) {
                        $scope.charts.liveFeed.series[i].visible = false;
                        if (typeof $scope.charts.liveFeed.yAxis[i] !== "undefined") {
                            $scope.charts.liveFeed.yAxis[i].visible = false;
                        }
                    }
                    // waiting here for one tick 'resets' the highcharts chart and prevents some weird overlaps
                    setTimeout(()=> {
                        if ($scope.hasConsumptionKwhData) $scope.isEntryActive["consumption"] = $scope.legendSettings["consumption"];
                        if ($scope.hasKwhData || $scope.hasPdcData || $scope.hasiOI1Data) $scope.isEntryActive["solar"] = $scope.legendSettings["solar"];
                        if ($scope.hasGeneratorKwhData) $scope.isEntryActive["generator"] = $scope.legendSettings["generator"];
                        if ($scope.hasBatteryData) $scope.isEntryActive["battery"] = $scope.legendSettings["battery"];
                        if ($scope.hasBvData) $scope.isEntryActive["batteryVoltage"] = $scope.legendSettings["batteryVoltage"];

                        // Left axis logic
                        // kwh data
                        if ($scope.hasKwhData) {
                            $scope.charts.liveFeed.yAxis[0].visible = true;
                            $scope.charts.liveFeed.series[0].visible = $scope.charts.liveFeed.series[0].data.length > 0 && $scope.isEntryActive["consumption"];
                            $scope.charts.liveFeed.series[1].visible = $scope.charts.liveFeed.series[1].data.length > 0 && $scope.isEntryActive["solar"];
                        } else if ($scope.hasPdcData) {
                            $scope.charts.liveFeed.yAxis[2].visible = $scope.isEntryActive["solar"];
                            $scope.charts.liveFeed.series[4].visible = $scope.charts.liveFeed.series[4].data.length > 0 && $scope.isEntryActive["solar"];
                        } else if ($scope.hasiOI1Data) {
                            $scope.charts.liveFeed.yAxis[4].visible = $scope.isEntryActive["solar"];
                            $scope.charts.liveFeed.series[5].visible = $scope.charts.liveFeed.series[5].data.length > 0 && $scope.isEntryActive["solar"];
                        } else if ($scope.hasBvData) {
                            $scope.bvEntryEnabled = true;
                            $scope.charts.liveFeed.yAxis[3].visible = true;
                            $scope.charts.liveFeed.series[6].visible = $scope.charts.liveFeed.series[6].data.length > 0 && $scope.isEntryActive["batteryVoltage"];
                            $scope.charts.liveFeed.series[7].visible = $scope.charts.liveFeed.series[7].data.length > 0 && $scope.isEntryActive["batteryVoltage"];
                        }
                        if ($scope.hasGeneratorKwhData) {
                            $scope.charts.liveFeed.yAxis[0].visible = $scope.isEntryActive["generator"];
                            $scope.charts.liveFeed.series[9].visible = $scope.charts.liveFeed.series[9].data.length > 0 && $scope.isEntryActive["generator"];
                        }


                        // Right axis logic
                        // bs (SOC)

                        if ($scope.hasSoc) {
                            $scope.charts.liveFeed.series[8].visible = $scope.isPropertyValid('bs') && ($scope.datePicker.range.endsNow || $scope.datePicker.range.keepRealtime) && $scope.charts.liveFeed.series[8].data.length === 2 && $scope.isEntryActive['battery'];
                            $scope.charts.liveFeed.yAxis[1].visible = true;
                            $scope.charts.liveFeed.series[2].visible = $scope.charts.liveFeed.series[2].data.length > 0 && $scope.isEntryActive['battery'];
                            $scope.charts.liveFeed.series[3].visible = $scope.charts.liveFeed.series[3].data.length > 0 && $scope.isEntryActive['battery'];
                        } else if ($scope.hasBvData && ($scope.hasKwhData || $scope.hasPdcData || $scope.hasiOI1Data)) {
                            $scope.bvEntryEnabled = true;
                            $scope.charts.liveFeed.yAxis[3].visible = true;
                            $scope.charts.liveFeed.yAxis[3].opposite = true;
                            $scope.charts.liveFeed.series[6].visible = $scope.charts.liveFeed.series[6].data.length > 0 && $scope.isEntryActive['batteryVoltage'];
                            $scope.charts.liveFeed.series[7].visible = $scope.charts.liveFeed.series[7].data.length > 0 && $scope.isEntryActive['batteryVoltage'];
                        }

                        // All liveFeed data processed, process forecast afterwards
                        $scope.toggleForecast($scope.showForecast, forceReload, false);
                    }, 0);
                } else if ($scope.graphs.current.id === 'consumption') {
                    $scope.charts.consumption.series[0].visible = true;
                    $scope.charts.consumption.series[1].visible = true;
                    $scope.charts.consumption.series[2].visible = true;
                    $scope.charts.consumption.series[3].visible = true;
                } else if ($scope.graphs.current.id === 'solar') {
                    $scope.charts.solar.series[0].visible = true;
                    $scope.charts.solar.series[1].visible = true;
                    $scope.charts.solar.series[2].visible = true;
                    $scope.charts.solar.series[3].visible = true;
                } else if ($scope.graphs.current.id === 'generator') {
                    $scope.charts.generator.series[0].visible = true;
                    $scope.charts.generator.series[1].visible = true;
                } else if ($scope.graphs.current.id === 'grid') {
                    $scope.charts.grid.series[0].visible = true;
                    $scope.charts.grid.series[1].visible = true;
                }
                $scope.charts[$scope.graphs.current.id].loading = false;
            });

            return true;
        }

        // A list of properties that triggers data reloading on change.
        $scope.$watchGroup(['datePicker.range.startDate', 'datePicker.range.endDate', 'graphs.current.id'], dateChangeObserver);

        // A list of properties that triggers DESS data reloading on change.
        $scope.$watchGroup(['dessDatePicker.range.startDate', 'ci.dem.rawValue'], dessDateChangeObserver);

        var consumptionLoaded = false;
        var solarLoaded = false;
        var generatorLoaded = false;
        var gridLoaded = false;

        $scope.$watch('graphs.current.id', function (chart) {
            if (chart == 'consumption' && !consumptionLoaded) {
                GraphService.getOverallStatsData($stateParams.id, 'consumption').then(function (data) {
                    for (var period in $scope.charts.consumptionPies) {
                        $scope.charts.consumptionPies[period].series[0].data = [data.records[period].totals.Gc || 0, data.records[period].totals.gc || 0, data.records[period].totals.Bc || 0, data.records[period].totals.Pc || 0];
                        $scope.charts.consumptionPies[period].series[0].__percentages = makePercentages($scope.charts.consumptionPies[period].series[0].data);
                    }
                    consumptionLoaded = true;
                }).catch(function () {
                    for (var period in $scope.charts.consumptionPies) {
                        $scope.charts.consumptionPies[period].series[0].data = [];
                        $scope.charts.consumptionPies[period].series[0].__percentages = [];
                    }
                }).then(function () {
                    for (var period in $scope.charts.consumptionPies) {
                        $scope.charts.consumptionPies[period].series[0].visible = true;
                        $scope.charts.consumptionPies[period].loading = false;
                    }
                });
            } else if (chart == 'solar' && !solarLoaded) {
                GraphService.getOverallStatsData($stateParams.id, 'solar_yield').then(function (data) {
                    for (var period in $scope.charts.solarPies) {
                        $scope.charts.solarPies[period].series[0].data = [data.records[period].totals.Pg || 0, data.records[period].totals.Pb || 0, data.records[period].totals.Pc || 0];
                        $scope.charts.solarPies[period].series[0].__percentages = makePercentages($scope.charts.solarPies[period].series[0].data);
                    }
                    solarLoaded = true;
                }).catch(function () {
                    for (var period in $scope.charts.solarPies) {
                        $scope.charts.solarPies[period].series[0].data = [];
                        $scope.charts.solarPies[period].series[0].__percentages = [];
                    }
                }).then(function () {
                    for (var period in $scope.charts.solarPies) {
                        $scope.charts.solarPies[period].series[0].visible = true;
                        $scope.charts.solarPies[period].loading = false;
                    }
                });
            } else if (chart === 'generator' && !generatorLoaded) {

                GraphService.getOverallStatsData($stateParams.id, 'generator-runtime').then(function (data) {
                    for (var period in $scope.generatorRuntimeProgress) {
                        $scope.generatorRuntimeProgress[period] = generatorRunTimeFormatter(data.records[period].totals.Tga, locale)
                    }
                }).catch(function () {
                    for (var period in $scope.generatorRuntimeProgress) {
                        $scope.generatorRuntimeProgress[period] = null;
                    }
                })
                GraphService.getOverallStatsData($stateParams.id, 'generator').then(function (data) {
                    for (var period in $scope.charts.generatorPies) {
                        $scope.charts.generatorPies[period].series[0].data = [data.records[period].totals.gb || 0, data.records[period].totals.gc || 0];
                        $scope.charts.generatorPies[period].series[0].__percentages = makePercentages($scope.charts.generatorPies[period].series[0].data);
                    }
                    generatorLoaded = true;
                }).catch(function () {
                    for (var period in $scope.charts.generatorPies) {
                        $scope.charts.generatorPies[period].series[0].data = [];
                        $scope.charts.generatorPies[period].series[0].__percentages = [];
                    }
                }).then(function () {
                    for (var period in $scope.charts.generatorPies) {
                        $scope.charts.generatorPies[period].series[0].visible = true;
                        $scope.charts.generatorPies[period].loading = false;
                    }
                });
            } else if (chart === 'grid' && !gridLoaded) {
                GraphService.getOverallStatsData($stateParams.id, 'grid').then(function (data) {
                    for (var period in $scope.charts.gridPies) {
                        $scope.charts.gridPies[period].series[0].data = [data.records[period].totals.Gb || 0, data.records[period].totals.Gc || 0];
                        $scope.charts.gridPies[period].series[0].__percentages = makePercentages($scope.charts.gridPies[period].series[0].data);
                    }
                    gridLoaded = true;
                }).catch(function () {
                    for (var period in $scope.charts.gridPies) {
                        $scope.charts.gridPies[period].series[0].data = [];
                        $scope.charts.gridPies[period].series[0].__percentages = [];
                    }
                }).then(function () {
                    for (var period in $scope.charts.gridPies) {
                        $scope.charts.gridPies[period].series[0].visible = true;
                        $scope.charts.gridPies[period].loading = false;
                    }
                });
            }
        });

        $scope.currentLoadingWatcher = undefined
        // Helper function for resetting the zoom of a chart.
        function resetZoomState(targetChart) {
            if (targetChart && targetChart.getHighcharts) { // It is null while updating
                var chart = targetChart.getHighcharts();
                // Wait until highcharts is loaded (again) before zooming out.
                if ($scope.currentLoadingWatcher) {
                    $scope.currentLoadingWatcher();
                }
                $scope.currentLoadingWatcher = $scope.$watch('targetChart.loading', function (current) {
                    if (current === false) {
                        chart.zoomOut();
                    }
                });
            } else if (targetChart) {
                // Small hack to wait until getHighCharts() becomes available
                _.defer(_.once(function () {
                    resetZoomState(targetChart);
                }));
            }
        }

        function isZoomedIn(targetChart) {
            if (targetChart && targetChart.getHighcharts) {
                var chart = targetChart.getHighcharts();
                return chart.resetZoomButton;
            }
        }

        triggerCIRebind(Object.keys($scope.ci));

        $scope.$on('$destroy', function () {
            angular.element(window).off('resize');
            // Clean up MQTT subscriptions
            unsubscribeMqtt();
        });
    });

    $scope.getDaysUntilAccurateForecast = function (lastLocationTimestamp) {
        let lastLocationChangeDate = moment.unix(lastLocationTimestamp)
        let daysSinceLastLocationChange = moment().diff(lastLocationChangeDate, 'days')

        return Math.max(0, DAYS_NEEDED_UNTIL_FORECAST_IS_ACCURATE - daysSinceLastLocationChange);
    }

    $scope.updateDataFlags = function () {
        if (!$scope.charts || !$scope.charts.liveFeed) return;
        $scope.hasKwhData = $scope.charts.liveFeed.series[0].data.length > 0 || $scope.charts.liveFeed.series[1].data.length > 0;
        $scope.hasConsumptionKwhData = $scope.charts.liveFeed.series[0].data.length > 0;
        $scope.hasSolarKwhData = $scope.charts.liveFeed.series[1].data.length > 0;
        $scope.hasGeneratorKwhData = $scope.charts.liveFeed.series[9].data.length > 0;
        $scope.hasSoc = $scope.charts.liveFeed.series[2].data.length > 0;
        $scope.hasBatteryData = $scope.hasSoc || $scope.charts.liveFeed.series[3].data.length > 0;
        $scope.hasPdcData = $scope.charts.liveFeed.series[4].data.length > 0;
        $scope.hasiOI1Data = $scope.charts.liveFeed.series[5].data.length > 0;
        $scope.hasBvData = $scope.charts.liveFeed.series[6].data.length > 0;
    }

    $scope.$watch('installation.geofenceEnabled', function (enabled, oldValue) {
        //Save geofence when changed
        if (oldValue !== undefined && enabled != oldValue) {
            ApiService.installationSettings($stateParams.id, {
                geofenceEnabled: enabled
            });
        }
    });

    $scope.flowsEnabled = false;
    $scope.$watch('installation.last_timestamp', function (value) {
        const getFlowsEnabled = () => {
            if (!value) return false;
            if ($scope.isRealtime) return true;
            const minutes = moment().diff(moment.unix(value), 'minutes');
            return minutes < 60;
        };
        $scope.flowsEnabled = getFlowsEnabled();
    });

    $scope.pieChartLegendStyle = function (chart, pieValue, $index) {
        const colors = chart.options.colors;
        const color = colors[$index % colors.length];
        return { color };
    };
    $scope.pieChartLegendPercentage = function (chart, pieValue, $index) {
        return sprintf('%.0f%%', chart.series[0].__percentages[$index]);
    };
    $scope.pieChartLegendValue = function (chart, pieValue, $index) {
        return sprintf('%.2f kWh', pieValue);
    };

    $scope.positionLines = function () {
        const leftColumnSecondLineParentHeight = $('.hub-ac-in').first().height();

        const blockAcIn = $('.block-ac_in').first();
        if (blockAcIn && blockAcIn.position()) {
            const topOffsetAcIn = blockAcIn.position().top + blockAcIn.height() / 2;
            const topOffsetFromParent = blockAcIn.offset().top - blockAcIn.parent().offset().top;
            const halfHeightBlock = blockAcIn.outerHeight() / 2;

            $('.hub-ac-in .line-hub-ac-in-multi.line-bt').css('top', topOffsetFromParent + halfHeightBlock);
            $('.hub-ac-in .line-hub-ac-in-multi.line-tb').css('top', topOffsetFromParent + halfHeightBlock);

            $('.line-hub-ac-in-2').css('top', topOffsetAcIn);
            $('.line-hub-ac-in').css('top', topOffsetAcIn);
        }

        const parallelAcIn = $('.parallel-ac-in').first();
        if (blockAcIn && blockAcIn.position() && parallelAcIn && parallelAcIn.position()) {
            const topOffset = (blockAcIn.height() / 2) - parallelAcIn.outerHeight(true);
            $('.line-ac-in-2').css('top', topOffset);
            $('.line-ac-in').css('top', topOffset);
        }

        const generatorBlock = $('#hub-generator');
        if (generatorBlock && leftColumnSecondLineParentHeight && generatorBlock.position()) {
            const topOffsetGenerator = leftColumnSecondLineParentHeight - generatorBlock.position().top - generatorBlock.height() / 2;
            const topOffsetFromParent = generatorBlock.offset().top - generatorBlock.parent().offset().top;
            const halfHeightBlock = generatorBlock.outerHeight() / 2;

            $('.hub-ac-in .line-hub-ac-in-multi.line-bt').css('top', topOffsetFromParent + halfHeightBlock);
            $('.hub-ac-in .line-hub-ac-in-multi.line-tb').css('top', topOffsetFromParent + halfHeightBlock);

            $('.line-hub-generator-2').css('bottom', topOffsetGenerator);
            $('.line-hub-generator').css('bottom', topOffsetGenerator);
        }

        const evChargingStationInBlock = $('#ev-car-charger-left');
        if (evChargingStationInBlock && leftColumnSecondLineParentHeight && evChargingStationInBlock.position()) {
            const topOffsetEv = leftColumnSecondLineParentHeight - evChargingStationInBlock.position().top - evChargingStationInBlock.height() / 2;
            $('.line-hub-generator-ev-charging-2').css('bottom', topOffsetEv);
            $('.line-hub-generator-ev-charging').css('bottom', topOffsetEv);
        }

        const blockAcLoadsParent = $('.parallel-ac-loads').first();
        const blockAcLoads = $('.block-ac_loads').first();
        const cellMulti = $('.cell.multi').first();
        if (cellMulti && cellMulti.position() && blockAcLoads && blockAcLoads.position()) {
            const topOffset = blockAcLoads.outerHeight(true) / 2;
            const topOffsetFromParent = blockAcLoads.offset().top - blockAcLoads.parent().offset().top;
            const halfHeightBlock = blockAcLoads.outerHeight() / 2;

            $('.line-ac-loads').css('top', - blockAcLoadsParent.outerHeight());
            $('.line-hub-ac-loads-3').css('top', topOffset);
            $('.line-hub-ac-loads-4').css('top', topOffset);
            $('.line-hub-ac-loads-7').css('top', topOffsetFromParent + halfHeightBlock);

            const height = cellMulti.outerHeight();
            $('.line-hub-inverter-3--ev').css('bottom', height - topOffset);
        }


        const criticalBlockParent = $('.critical_loads').first();
        const criticalBlock = $('.block-critical_loads').first();
        if (criticalBlockParent && criticalBlockParent.position() && criticalBlock && criticalBlock.position()) {
            const topOffset = (criticalBlock.height() / 2) - criticalBlockParent.outerHeight(true)

            $('.line-critical-loads').css('top', topOffset);
            $('.pv-charger-and-ev-charger').css('top', topOffset);
            $('.line-to-critical-loads').css('top', topOffset);
        }

        const pvInverterInBlock = $('.parallel-inverter-ac-in>.block-pv_inverter').first();
        if (pvInverterInBlock && pvInverterInBlock.position()) {
            const middle = pvInverterInBlock.height() / 2;
            $('.line-ac-pvinv--ev').css('bottom', -middle);
            $('.line-ac-pvinv-2').css('top', `calc(100% + ${middle}px - 4px)`);
        }

        const evChargingStationOutBlock = $('#ev-car-charger-right');
        const hubAcLoads = $('.hub-ac-loads').first();
        if (hubAcLoads && hubAcLoads.position() && evChargingStationOutBlock && evChargingStationOutBlock.position()) {
            const topOffsetEv = hubAcLoads.height() - evChargingStationOutBlock.position().top - evChargingStationOutBlock.height() / 2;
            $('.line-hub-ac-loads-5').css('bottom', topOffsetEv);
        }

        const loweredBattery = $('.cell.battery.lowered').first();
        if (loweredBattery && loweredBattery.position() && cellMulti && cellMulti.position()) {
            const multiTopOffset = cellMulti.offset().top;
            const loweredBatteryTopOffset = loweredBattery.offset().top;
            const spaceBetween = loweredBatteryTopOffset - multiTopOffset - cellMulti.outerHeight();
            $('.line-multi-dc.lowered--pv-inverter').css('bottom', `calc(-${spaceBetween}px - 3rem)`);
            if ($scope.prevLoweredPvInverterOffset !== $('.line-multi-dc.lowered--pv-inverter').css('bottom')) {
                $scope.prevLoweredPvInverterOffset = $('.line-multi-dc.lowered--pv-inverter').css('bottom');
                $scope.rerenderLoweresdPvInvPipe();
            }
        } else {
            // Make sure to remove the attribute if topology changes
            if ($scope.prevLoweredPvInverterOffset !== null) {
                $('.line-multi-dc').css('bottom', '');
                $scope.prevLoweredPvInverterOffset = null;
                $scope.rerenderLoweresdPvInvPipe();
            }
        }
    }

    // Rerender lowered pv inverter pipe to fix dot flow
    $scope.rerenderLoweresdPvInvPipe = function () {
        $('.line-multi-dc').css('display', 'none');
        $scope.$evalAsync();
        $timeout(function () {
            $('.line-multi-dc').css('display', '');
            $scope.$evalAsync();
        }, 0)
    }

    $scope.overlay = document.getElementsByClassName('vrm-nav__overlay')[0];
    $scope.triggerCloseClick = function () {
        const closeButton = document.getElementsByClassName(
            "sg-btn vrm-control-settings"
        )[0];
        if (closeButton.click()) closeButton.click();
    };
    // Device controls related utilities and watchers
    $scope.closeControlDevices = function () {
        $scope.overlay.style.display = "none";
        $scope.overlay.style.zIndex = "200";
        $scope.overlay.removeEventListener("click", $scope.triggerCloseClick);

        $scope.controlDevicesDrawerOpen = false;

        // Refresh 3 times the site, in case the changes did not take make it the first time or the second time
        const FIFTEEN_SECONDS = 15 * 1000;
        for (let i = 1; i <= 3; i++) {
            setTimeout(() => {
                $scope.refreshInstallation();
            }, i * FIFTEEN_SECONDS);
        }
    };
    $scope.openControlDevices = function () {
        $scope.overlay.style.display = "block";
        $scope.overlay.style.zIndex = "5";
        $scope.overlay.addEventListener("click", $scope.triggerCloseClick);

        $scope.controlDevicesDrawerOpen = true;
    };

    $scope.$watch('isRealtime', function () {
        if (!$scope.isRealtime) {
            $scope.hideDeviceControlsTimer = $timeout(function () {
                // installation not realtime, close drawer and hide button
                if ($scope.controlDevicesDrawerOpen) {
                    vrmNotificationsService.add({
                        type: vrmNotificationType.INFO,
                        message: locale.getString('dashboard.controls_realtime_not_available')
                    });
                }
                $scope.closeControlDevices();
                $scope.hideDeviceControls = true;
            }, 10 * 1000);
        } else {
            const isCancelled = $timeout.cancel($scope.hideDeviceControlsTimer);
            if (!isCancelled) {
                // revert timeout changes
                $scope.hideDeviceControls = false;
            }
            $scope.hideDeviceControls = null;
        }
    })
    $scope.$on('$destroy', function () {
        $timeout.cancel($scope.hideDeviceControlsTimer);
    });

    $scope.setHasDeviceControls = function (hasDeviceControls) {
        $scope.hasDeviceControls = hasDeviceControls;
    }

    // openControls query param is part of the dashboard URL
    // and angular thinks that /dashboard and /dashboard?openControls=1 are different and
    // does a hard reload upon change
    // thus we use mobileOpenControls param to open the drawer from the mobile apps
    // that way when we clear the query param the page is not reloaded
    $scope.$watchGroup(['location.search()'], function () {
        $scope.controlDevicesDrawerOpen = !!($location.search().openControls) || !!($location.search().mobileOpenControls);
        if ($location.search().openControls) {
            $location.search('openControls', null);
        }
    })

    function makePercentages(values) {
        // Based on: https://stackoverflow.com/questions/13483430/how-to-make-rounded-percentages-add-up-to-100
        const total = values.reduce((a, b) => a + b);
        // In case everything is zero then just mark everything as 0%
        if (total === 0) {
            return values.map(() => 0);
        }
        values = values.map(x => x / total * 100);
        const target = 100;
        const offset = target - _.reduce(values, function (acc, x) { return acc + Math.round(x) }, 0);
        return _.chain(values)
            .map((value, index) => [index, value])
            .sortBy(([_, x]) => Math.round(x) - x)
            .map(([index, x], i) => [index, Math.round(x) + (offset > i) - (i >= (values.length + offset))])
            .sortBy(([index, _]) => index)
            .map(([_, value]) => value)
            .value();
    }

    function getMpptsInOrder() {
        $scope.mppts = [];
        $scope.mpptsRS = [];
        $scope.multiRS = [];
        $scope.inverterRS = [];
        const ci = $scope.ci;

        if (ci.PVP.hasOwnProperty('instances')) {
            for (let instance of Object.keys(ci.PVP.instances)) {
                if (!ci.PVV0 || !ci.PVV0.instances || !ci.PVV0.instances[instance]) {
                    $scope.mppts.push(formatMPPTAttributes(ci, instance));
                }
                if (ci.PVV0 && ci.PVV0.instances && ci.PVV0.instances[instance]) {
                    $scope.mpptsRS = $scope.mpptsRS.concat(formatRSMPPTAttributes(ci, instance));
                }
            }
        }
        if (ci.mPVP.hasOwnProperty('instances')) {
            for (let instance of Object.keys(ci.mPVP.instances)) {
                $scope.multiRS = $scope.multiRS.concat(formatMultiRsAttributes(ci, instance))
            }
        }
        if (ci.iPVP.hasOwnProperty('instances')) {
            for (let instance of Object.keys(ci.iPVP.instances)) {
                $scope.inverterRS = $scope.inverterRS.concat(formatInverterRsAttributes(ci, instance))
            }
        }

        $scope.mppts = $scope.mppts.sort((a, b) => a.label.localeCompare(b.label, undefined, { numeric: true, sensitivity: 'base' }))
        $scope.mpptsRS = $scope.mpptsRS.sort((a, b) => a.label.localeCompare(b.label, undefined, { numeric: true, sensitivity: 'base' }))
        $scope.multiRS = $scope.multiRS.sort((a, b) => a.label.localeCompare(b.label, undefined, { numeric: true, sensitivity: 'base' }))
        $scope.inverterRS = $scope.inverterRS.sort((a, b) => a.label.localeCompare(b.label, undefined, { numeric: true, sensitivity: 'base' }))
        $scope.$broadcast('$$rebind::mppts')

        const allMpptsDisabled = $scope.mppts.every(instance => instance.isEnabled === 0);
        const allMpptsRsDisabled = $scope.mpptsRS.every(instance => instance.isEnabled === 0);
        const allMultiRsDisabled = $scope.multiRS.every(instance => instance.isEnabled === 0);
        const allInverterRsDisabled = $scope.inverterRS.every(instance => instance.isEnabled === 0);

        $scope.notAllTrackersDisabled = !allMpptsDisabled || !allMpptsRsDisabled || !allMultiRsDisabled || !allInverterRsDisabled;
    }

    function formatMPPTAttributes(ci, instance) {
        var mppt = {
            label: "",
            volts: "",
            amps: "",
            watts: "",
            isEnabled: 1
        }

        if (!ci.Sccn.instances || !ci.Sccn.instances[instance] || !ci.Sccn.instances[instance].rawValue) {
            mppt.label = "MPPT-" + instance;
        } else {
            mppt.label = ci.Sccn.instances[instance].formattedValue;
        }
        if (ci.PVV.instances && ci.PVV.instances && ci.PVV.instances[instance]) {
            mppt.volts = getFormattedValueForInstance(ci.PVV, instance);
        } else {
            mppt.volts = "-";
        }
        if (ci.PVP.instances && ci.PVP.instances[instance] && ci.PVV.instances && ci.PVV.instances[instance]) {
            mppt.amps = $scope.calculateCurrent(ci.PVP.instances[instance].rawValue,
                ci.PVV.instances[instance].rawValue, ci.PVP.instances[instance].timestamp)
        } else {
            mppt.amps = "-";
        }
        if (ci.PVP.instances && ci.PVP.instances[instance]) {
            mppt.watts = getFormattedValueForInstance(ci.PVP, instance);
        }
        return mppt;
    }

    function formatRSMPPTAttributes(ci, instance) {
        let mpptRS = {};
        let formattedAttributes = [];
        if (!ci.PVP0 || !ci.PVP0.instances || !ci.PVP0.instances[instance]) {
            //MPPT RS without per tracker power
            if (!ci.Sccn.instances || !ci.Sccn.instances[instance] || !ci.Sccn.instances[instance].rawValue) {
                mpptRS.label = "MPPT-" + instance;
            } else {
                mpptRS.label = ci.Sccn.instances[instance].formattedValue;
            }
            mpptRS.volts = ' ';
            mpptRS.amps = ' ';
            mpptRS.watts = getFormattedValueForInstance(ci.PVP, instance);
            mpptRS.isEnabled = 1;
            formattedAttributes.push(mpptRS);
            mpptRS = {};
        }
        if (ci.PVP0 && ci.PVP0.instances && ci.PVP0.instances[instance]) {
            //MPPT RS with power per tracker
            if (!ci.Sccn.instances || !ci.Sccn.instances[instance] || !ci.Sccn.instances[instance].rawValue) {
                mpptRS.label = "MPPT-" + instance;
            } else {
                mpptRS.label = ci.Sccn.instances[instance].formattedValue;
            }
            if (ci.PVn0 && ci.PVn0.instances && ci.PVn0.instances[instance]) {
                mpptRS.label += '-' + ci.PVn0.instances[instance].formattedValue;
            } else {
                mpptRS.label += "-#1";
            }
            mpptRS.volts = getFormattedValueForInstance(ci.PVV0, instance);
            mpptRS.amps = $scope.calculateCurrent(ci.PVP0.instances[instance].rawValue,
                ci.PVV0.instances[instance].rawValue, ci.PVP0.instances[instance].timestamp);
            mpptRS.watts = getFormattedValueForInstance(ci.PVP0, instance);
            if (ci.PVe0) {
                mpptRS.isEnabled = (ci.PVe0.instances && ci.PVe0.instances[instance]) ? ci.PVe0.instances[instance].rawValue : 1;
            }else {
                mpptRS.isEnabled = 1;
            }
            formattedAttributes.push(mpptRS);
            mpptRS = {};
        }
        if (ci.PVP1 && ci.PVP1.instances && ci.PVP1.instances[instance]) {
            if (!ci.Sccn.instances || !ci.Sccn.instances[instance] || !ci.Sccn.instances[instance].rawValue) {
                mpptRS.label = "MPPT-" + instance;
            } else {
                mpptRS.label = ci.Sccn.instances[instance].formattedValue;
            }

            if (ci.PVn1 && ci.PVn1.instances && ci.PVn1.instances[instance]) {
                mpptRS.label += '-' + ci.PVn1.instances[instance].formattedValue;
            } else {
                mpptRS.label += "-#2";
            }

            mpptRS.volts = getFormattedValueForInstance(ci.PVV1, instance);
            mpptRS.amps = $scope.calculateCurrent(ci.PVP1.instances[instance].rawValue,
                ci.PVV1.instances[instance].rawValue, ci.PVP1.instances[instance].timestamp);
            mpptRS.watts = getFormattedValueForInstance(ci.PVP1, instance);
            if (ci.PVe1) {
                mpptRS.isEnabled = (ci.PVe1.instances && ci.PVe1.instances[instance]) ? ci.PVe1.instances[instance].rawValue : 1;
            } else {
                mpptRS.isEnabled = 1;
            }
            formattedAttributes.push(mpptRS);
            mpptRS = {};
        }
        if (ci.PVP2 && ci.PVP2.instances && ci.PVP2.instances[instance]) {
            if (!ci.Sccn.instances || !ci.Sccn.instances[instance] || !ci.Sccn.instances[instance].rawValue) {
                mpptRS.label = "MPPT-" + instance;
            } else {
                mpptRS.label = ci.Sccn.instances[instance].formattedValue;
            }

            if (ci.PVn2 && ci.PVn2.instances && ci.PVn2.instances[instance]) {
                mpptRS.label += '-' + ci.PVn2.instances[instance].formattedValue;
            } else {
                mpptRS.label += "-#3";
            }

            mpptRS.volts = getFormattedValueForInstance(ci.PVV2, instance);
            mpptRS.amps = $scope.calculateCurrent(ci.PVP2.instances[instance].rawValue,
                ci.PVV2.instances[instance].rawValue, ci.PVP2.instances[instance].timestamp);
            mpptRS.watts = getFormattedValueForInstance(ci.PVP2, instance);
            if (ci.PVe2) {
                mpptRS.isEnabled = (ci.PVe2.instances && ci.PVe2.instances[instance]) ? ci.PVe2.instances[instance].rawValue : 1;
            } else {
                mpptRS.isEnabled = 1;
            }
            formattedAttributes.push(mpptRS);
            mpptRS = {};
        }
        if (ci.PVP3 && ci.PVP3.instances && ci.PVP3.instances[instance]) {
            if (!ci.Sccn.instances || !ci.Sccn.instances[instance] || !ci.Sccn.instances[instance].rawValue) {
                mpptRS.label = "MPPT-" + instance;
            } else {
                mpptRS.label = ci.Sccn.instances[instance].formattedValue;
            }

            if (ci.PVn3 && ci.PVn3.instances && ci.PVn3.instances[instance]) {
                mpptRS.label += '-' + ci.PVn3.instances[instance].formattedValue;
            } else {
                mpptRS.label += "-#4";
            }

            mpptRS.volts = getFormattedValueForInstance(ci.PVV3, instance);
            mpptRS.amps = $scope.calculateCurrent(ci.PVP3.instances[instance].rawValue,
                ci.PVV3.instances[instance].rawValue, ci.PVP3.instances[instance].timestamp);
            mpptRS.watts = getFormattedValueForInstance(ci.PVP3, instance);
            if (ci.PVe3) {
                mpptRS.isEnabled = (ci.PVe3.instances && ci.PVe3.instances[instance]) ? ci.PVe3.instances[instance].rawValue : 1;
            } else {
                mpptRS.isEnabled = 1;
            }
            formattedAttributes.push(mpptRS);
        }
        return formattedAttributes;
    }

    function formatMultiRsAttributes(ci, instance) {
        let multiRS = {}
        let formattedAttributes = []

        if (!ci.mPVP0 || !ci.mPVP0.instances || !ci.mPVP0.instances[instance]) {
            // No per tracker power
            if (!ci.mcn.instances || !ci.mcn.instances[instance] || !ci.mcn.instances[instance].rawValue) {
                multiRS.label = "MultiRS-" + instance;
            } else {
                multiRS.label = ci.mcn.instances[instance].formattedValue;
            }
            multiRS.volts = getFormattedValueForInstance(ci.mPVV, instance);
            multiRS.amps = $scope.calculateCurrent(ci.mPVP.instances[instance].rawValue,
                ci.mPVV.instances[instance].rawValue, ci.mPVP.instances[instance].timestamp);
            multiRS.watts = getFormattedValueForInstance(ci.mPVP, instance);
            multiRS.isEnabled = 1;
            formattedAttributes.push(multiRS);
            return formattedAttributes
        }

        if (ci.mPVP0 && ci.mPVP0.instances && ci.mPVP0.instances[instance]) {
            if (!ci.mcn.instances || !ci.mcn.instances[instance] || !ci.mcn.instances[instance].rawValue) {
                multiRS.label = "MultiRS-" + instance;
            } else {
                multiRS.label = ci.mcn.instances[instance].formattedValue;
            }

            if (ci.mPVn0 && ci.mPVn0.instances && ci.mPVn0.instances[instance]) {
                multiRS.label += '-' + ci.mPVn0.instances[instance].formattedValue;
            } else {
                multiRS.label += "-#1"
            }

            multiRS.volts = getFormattedValueForInstance(ci.mPVV0, instance);
            multiRS.amps = $scope.calculateCurrent(ci.mPVP0.instances[instance].rawValue,
                ci.mPVV0.instances[instance].rawValue, ci.mPVP0.instances[instance].timestamp);
            multiRS.watts = getFormattedValueForInstance(ci.mPVP0, instance);
            if (ci.mPVe0) {
                multiRS.isEnabled = (ci.mPVe0.instances && ci.mPVe0.instances[instance]) ? ci.mPVe0.instances[instance].rawValue : 1;
            } else {
                multiRS.isEnabled = 1;
            }
            formattedAttributes.push(multiRS);
            multiRS = {};
        }
        if (ci.mPVP1 && ci.mPVP1.instances && ci.mPVP1.instances[instance]) {
            if (!ci.mcn.instances || !ci.mcn.instances[instance] || !ci.mcn.instances[instance].rawValue) {
                multiRS.label = "MultiRS-" + instance;
            } else {
                multiRS.label = ci.mcn.instances[instance].formattedValue;
            }

            if (ci.mPVn1 && ci.mPVn1.instances && ci.mPVn1.instances[instance]) {
                multiRS.label += '-' + ci.mPVn1.instances[instance].formattedValue;
            } else {
                multiRS.label += "-#2";
            }

            multiRS.volts = getFormattedValueForInstance(ci.mPVV1, instance);
            multiRS.amps = $scope.calculateCurrent(ci.mPVP1.instances[instance].rawValue,
                ci.mPVV1.instances[instance].rawValue, ci.mPVP1.instances[instance].timestamp);
            multiRS.watts = getFormattedValueForInstance(ci.mPVP1, instance);
            if (ci.mPVe1) {
                multiRS.isEnabled = (ci.mPVe1.instances && ci.mPVe1.instances[instance]) ? ci.mPVe1.instances[instance].rawValue : 1;
            } else {
                multiRS.isEnabled = 1;
            }
            formattedAttributes.push(multiRS);
            multiRS = {};
        }
        if (ci.mPVP2 && ci.mPVP2.instances && ci.mPVP2.instances[instance]) {
            if (!ci.mcn.instances || !ci.mcn.instances[instance] || !ci.mcn.instances[instance].rawValue) {
                multiRS.label = "MultiRS-" + instance;
            } else {
                multiRS.label = ci.mcn.instances[instance].formattedValue;
            }

            if (ci.mPVn2 && ci.mPVn2.instances && ci.mPVn2.instances[instance]) {
                multiRS.label += '-' + ci.mPVn2.instances[instance].formattedValue;
            } else {
                multiRS.label += "-#3";
            }

            multiRS.volts = getFormattedValueForInstance(ci.mPVV2, instance);
            multiRS.amps = $scope.calculateCurrent(ci.mPVP2.instances[instance].rawValue,
                ci.mPVV2.instances[instance].rawValue, ci.mPVP2.instances[instance].timestamp);
            multiRS.watts = getFormattedValueForInstance(ci.mPVP2, instance);
            if (ci.mPVe2) {
                multiRS.isEnabled = (ci.mPVe2.instances && ci.mPVe2.instances[instance]) ? ci.mPVe2.instances[instance].rawValue : 1;
            } else {
                multiRS.isEnabled = 1;
            }
            formattedAttributes.push(multiRS);
            multiRS = {};
        }
        if (ci.mPVP3 && ci.mPVP3.instances && ci.mPVP3.instances[instance]) {
            if (!ci.Sccn.instances || !ci.Sccn.instances[instance] || !ci.Sccn.instances[instance].rawValue) {
                multiRS.label = "MultiRS-" + instance;
            } else {
                multiRS.label = ci.mcn.instances[instance].formattedValue;
            }

            if (ci.mPVn3 && ci.mPVn3.instances && ci.mPVn3.instances[instance]) {
                multiRS.label += '-' + ci.mPVn3.instances[instance].formattedValue;
            } else {
                multiRS.label += "-#4";
            }

            multiRS.volts = getFormattedValueForInstance(ci.mPVV3, instance);
            multiRS.amps = $scope.calculateCurrent(ci.mPVP3.instances[instance].rawValue,
                ci.mPVV3.instances[instance].rawValue, ci.mPVP3.instances[instance].timestamp);
            multiRS.watts = getFormattedValueForInstance(ci.mPVP3, instance);
            if (ci.mPVe3) {
                multiRS.isEnabled = (ci.mPVe3.instances && ci.mPVe3.instances[instance]) ? ci.mPVe3.instances[instance].rawValue : 1;
            } else {
                multiRS.isEnabled = 1;
            }
            formattedAttributes.push(multiRS);
        }
        return formattedAttributes
    }

    function formatInverterRsAttributes(ci, instance) {
        let inverterRS = {}
        let formattedAttributes = []

        if (!ci.iPVP0 || !ci.iPVP0.instances || !ci.iPVP0.instances[instance]) {
            // No per tracker power
            if (!ci.icn.instances || !ci.icn.instances[instance] || !ci.icn.instances[instance].rawValue) {
                inverterRS.label = "InverterRS-" + instance;
            } else {
                inverterRS.label = ci.icn.instances[instance].formattedValue;
            }
            inverterRS.volts = getFormattedValueForInstance(ci.iPVV, instance);
            inverterRS.amps = $scope.calculateCurrent(ci.iPVP.instances[instance].rawValue,
                ci.iPVV.instances[instance].rawValue, ci.iPVP.instances[instance].timestamp);
            inverterRS.watts = getFormattedValueForInstance(ci.iPVP, instance);
            inverterRS.isEnabled = 1;
            formattedAttributes.push(inverterRS);
            return formattedAttributes
        }
        if (ci.iPVP0 && ci.iPVP0.instances && ci.iPVP0.instances[instance]) {
            if (!ci.icn.instances || !ci.icn.instances[instance] || !ci.icn.instances[instance].rawValue) {
                inverterRS.label = "InverterRS-" + instance;
            } else {
                inverterRS.label = ci.icn.instances[instance].formattedValue;
            }

            if (ci.iPVn0 && ci.iPVn0.instances && ci.iPVn0.instances[instance]) {
                inverterRS.label += '-' + ci.iPVn0.instances[instance].formattedValue;
            } else {
                inverterRS.label += "-#1";
            }

            inverterRS.volts = getFormattedValueForInstance(ci.iPVV0, instance);
            inverterRS.amps = $scope.calculateCurrent(ci.iPVP0.instances[instance].rawValue,
                ci.iPVV0.instances[instance].rawValue, ci.iPVP0.instances[instance].timestamp);
            inverterRS.watts = getFormattedValueForInstance(ci.iPVP0, instance);
            if (ci.iPVe0) {
                inverterRS.isEnabled = (ci.iPVe0.instances && ci.iPVe0.instances[instance]) ? ci.iPVe0.instances[instance].rawValue : 1;
            } else {
                inverterRS.isEnabled = 1;
            }
            formattedAttributes.push(inverterRS);
            inverterRS = {};
        }
        if (ci.iPVP1 && ci.iPVP1.instances && ci.iPVP1.instances[instance]) {
            if (!ci.icn.instances || !ci.icn.instances[instance] || !ci.icn.instances[instance].rawValue) {
                inverterRS.label = "InverterRS-" + instance;
            } else {
                inverterRS.label = ci.icn.instances[instance].formattedValue;
            }

            if (ci.iPVn1 && ci.iPVn1.instances && ci.iPVn1.instances[instance]) {
                inverterRS.label += '-' + ci.iPVn1.instances[instance].formattedValue;
            } else {
                inverterRS.label += "-#2";
            }

            inverterRS.volts = getFormattedValueForInstance(ci.iPVV1, instance);
            inverterRS.amps = $scope.calculateCurrent(ci.iPVP1.instances[instance].rawValue,
                ci.iPVV1.instances[instance].rawValue, ci.iPVP1.instances[instance].timestamp);
            inverterRS.watts = getFormattedValueForInstance(ci.iPVP1, instance);
            if (ci.iPVe1) {
                inverterRS.isEnabled = (ci.iPVe1.instances && ci.iPVe1.instances[instance]) ? ci.iPVe1.instances[instance].rawValue : 1;
            } else {
                inverterRS.isEnabled = 1;
            }
            formattedAttributes.push(inverterRS);
            inverterRS = {};
        }
        if (ci.iPVP2 && ci.iPVP2.instances && ci.iPVP2.instances[instance]) {
            if (!ci.icn.instances || !ci.icn.instances[instance] || !ci.icn.instances[instance].rawValue) {
                inverterRS.label = "InverterRS-" + instance;
            } else {
                inverterRS.label = ci.icn.instances[instance].formattedValue;
            }

            if (ci.iPVn2 && ci.iPVn2.instances && ci.iPVn2.instances[instance]) {
                inverterRS.label += '-' + ci.iPVn2.instances[instance].formattedValue;
            } else {
                inverterRS.label += "-#3";
            }

            inverterRS.volts = getFormattedValueForInstance(ci.iPVV2, instance);
            inverterRS.amps = $scope.calculateCurrent(ci.iPVP2.instances[instance].rawValue,
                ci.iPVV2.instances[instance].rawValue, ci.iPVP2.instances[instance].timestamp);
            inverterRS.watts = getFormattedValueForInstance(ci.iPVP2, instance);
            if (ci.iPVe2) {
                inverterRS.isEnabled = (ci.iPVe2.instances && ci.iPVe2.instances[instance]) ? ci.iPVe2.instances[instance].rawValue : 1;
            } else {
                inverterRS.isEnabled = 1;
            }
            formattedAttributes.push(inverterRS);
            inverterRS = {};
        }
        if (ci.iPVP3 && ci.iPVP3.instances && ci.iPVP3.instances[instance]) {
            if (!ci.Sccn.instances || !ci.Sccn.instances[instance] || !ci.Sccn.instances[instance].rawValue) {
                inverterRS.label = "InverterRS-" + instance;
            } else {
                inverterRS.label = ci.icn.instances[instance].formattedValue;
            }

            if (ci.iPVn3 && ci.iPVn3.instances && ci.iPVn3.instances[instance]) {
                inverterRS.label += '-' + ci.iPVn3.instances[instance].formattedValue;
            } else {
                inverterRS.label += "-#4";
            }

            inverterRS.volts = getFormattedValueForInstance(ci.iPVV3, instance);
            inverterRS.amps = $scope.calculateCurrent(ci.iPVP3.instances[instance].rawValue,
                ci.iPVV3.instances[instance].rawValue, ci.iPVP3.instances[instance].timestamp);
            inverterRS.watts = getFormattedValueForInstance(ci.iPVP3, instance);
            if (ci.iPVe3) {
                inverterRS.isEnabled = (ci.iPVe3.instances && ci.iPVe3.instances[instance]) ? ci.iPVe3.instances[instance].rawValue : 1;
            } else {
                inverterRS.isEnabled = 1;
            }
            formattedAttributes.push(inverterRS);
        }
        return formattedAttributes
    }

    $scope.pickValueOfFirstInstance = function (attribute) {
        if (!(attribute && attribute.instances)) {
            return {};
        }
        if (Array.isArray(attribute.instances)) {
            return attribute.instances[0];
        }
        return Object.values(attribute.instances)[0];
    }


    $rootScope.$emit('checkMapsAvailabilityEvent');

    $scope.dismissForecastWarning = function () {
        $scope.showForecastWarning = false;

        localStorageService.set(HIDE_FORECAST_WARNING_STORAGEKEY_TEMPLATE.replace('*', $scope.installation.id), {hide: true, timestamp: $scope.last_location_timestamp});
    }

    $scope.activateForecastWarning = function () {
        const oldValue = localStorageService.get(HIDE_FORECAST_WARNING_STORAGEKEY_TEMPLATE.replace('*', $scope.installation.id))

        if(oldValue && oldValue.timestamp !== $scope.last_location_timestamp) {
            // if new timestamp then show new warning
            $scope.showForecastWarning = true;
            localStorageService.set(HIDE_FORECAST_WARNING_STORAGEKEY_TEMPLATE.replace('*', $scope.installation.id), {hide: false, timestamp: $scope.last_location_timestamp});
        }

    }

    $scope.getCorrectLastLocationTimestamp = async function (id = $scope.installation.id){
        try{
            return !!id && await ApiService.getCacheLocation(id).then(data => {
                if(data && data.data){
                    return data.data.last_location_timestamp;
                }
                return false
            })
        } catch (e) {
            return false
        }
    }

    $scope.isEntryActive = {};

    $scope.isNoEntryActive = function (entries) {
        return !Object.values(entries).reduce((acc, val) => acc || val, false);
    }

    $scope.isEveryEntryActive = function (entries) {
        return Object.values(entries).reduce((acc, val) => acc && val, true);
    }

    function updateLegend() {
        if (!$scope.charts || !$scope.charts.liveFeed) return;
        $scope.updateDataFlags();
        if ($scope.hasConsumptionKwhData) {
            $scope.charts.liveFeed.series[0].visible = $scope.charts.liveFeed.series[0].data.length > 0 && $scope.isEntryActive['consumption'];
        } else {
            delete $scope.isEntryActive["consumption"];
        }
        if ($scope.hasKwhData) {
            $scope.charts.liveFeed.series[1].visible = $scope.charts.liveFeed.series[1].data.length > 0 && $scope.isEntryActive['solar'];
        } else if ($scope.hasPdcData) {
            $scope.charts.liveFeed.series[4].visible = $scope.charts.liveFeed.series[4].data.length > 0 && $scope.isEntryActive['solar'];
        } else if ($scope.hasiOI1Data) {
            $scope.charts.liveFeed.series[5].visible = $scope.charts.liveFeed.series[5].data.length > 0 && $scope.isEntryActive['solar'];
        } else if ($scope.hasBvData) {
            delete $scope.isEntryActive["solar"];
            $scope.charts.liveFeed.series[6].visible = $scope.charts.liveFeed.series[6].data.length > 0 && $scope.isEntryActive['batteryVoltage'];
            $scope.charts.liveFeed.series[7].visible = $scope.charts.liveFeed.series[7].data.length > 0 && $scope.isEntryActive['batteryVoltage'];
        } else {
            delete $scope.isEntryActive["batteryVoltage"];
        }
        if ($scope.hasGeneratorKwhData) {
            $scope.charts.liveFeed.series[9].visible = $scope.charts.liveFeed.series[9].data.length > 0 && $scope.isEntryActive['generator'];
        } else {
            delete $scope.isEntryActive["generator"];
        }
        if ($scope.hasSoc) {
            $scope.charts.liveFeed.series[8].visible = $scope.isPropertyValid('bs') && ($scope.datePicker.range.endsNow || $scope.datePicker.range.keepRealtime) && $scope.charts.liveFeed.series[8].data.length === 2 && $scope.isEntryActive['battery'];
            $scope.charts.liveFeed.series[2].visible = $scope.charts.liveFeed.series[2].data.length > 0 && $scope.isEntryActive['battery'];
            $scope.charts.liveFeed.series[3].visible = $scope.charts.liveFeed.series[3].data.length > 0 && $scope.isEntryActive['battery'];
            $scope.charts.liveFeed.series[12].visible = $scope.canHaveForecast && $scope.charts.liveFeed.series[12].data.length > 0 && $scope.isEntryActive['battery'];
        } else if ($scope.hasBvData && ($scope.hasKwhData || $scope.hasPdcData || $scope.hasiOI1Data)) {
            delete $scope.isEntryActive["battery"];
            $scope.charts.liveFeed.series[6].visible = $scope.charts.liveFeed.series[6].data.length > 0 && $scope.isEntryActive['batteryVoltage'];
            $scope.charts.liveFeed.series[7].visible = $scope.charts.liveFeed.series[7].data.length > 0 && $scope.isEntryActive['batteryVoltage'];
        } else {
            delete $scope.isEntryActive["batteryVoltage"];
        }
        if ($scope.hasSolarForecastData) {
            $scope.charts.liveFeed.series[10].visible = $scope.isEntryActive['forecastSolar'];
        } else {
            delete $scope.isEntryActive["forecastSolar"];
        }
        if ($scope.hasConsumptionForecastData) {
            $scope.charts.liveFeed.series[11].visible = $scope.isEntryActive['forecastConsumption'];
        } else {
            delete $scope.isEntryActive["forecastConsumption"];
        }
        if ($scope.isEntryActive['forecastSolar'] || $scope.isEntryActive['forecastConsumption']) {
            const [from, to] = nowIndicatorInterval();
            $scope.charts.liveFeed.xAxis.plotBands[0].from = from;
            $scope.charts.liveFeed.xAxis.plotBands[0].to = to;
        }
        if ($scope.showForecast && !$scope.isEntryActive['forecastSolar'] && !$scope.isEntryActive['forecastConsumption']) {
            $scope.resetNowInidcator();
        }
    }

    $scope.resetNowInidcator = function () {
        $scope.charts.liveFeed.xAxis.plotBands[0].from = null;
        $scope.charts.liveFeed.xAxis.plotBands[0].to = null;
    }

    $scope.onLegendToggle = function (legendEntryName) {
        $scope.isEntryActive[legendEntryName] = !$scope.isEntryActive[legendEntryName];

        updateLegend();
        $scope.updateLegendSettings();
    }

    // Initialize this to the default values, they will get overwritten once the API call comes back
    $scope.legendSettings = {
        'consumption': true,
        'solar': true,
        'generator': true,
        'battery': true,
        'batteryVoltage': true,
        'forecastSolar': true,
        'forecastConsumption': false
    };

    ApiService.installations($stateParams.id, 'dashboard-graph-settings').then(function (response) {
        if (!response.data || !response.data.dashboardSetting) return;
        for (const key in $scope.legendSettings) {
            if (response.data.dashboardSetting.hasOwnProperty(key)) {
                $scope.legendSettings[key] = parseBoolean(response.data.dashboardSetting[key]);
            }
        }
    });

    $scope.updateLegendSettings = function () {
        $scope.legendSettings = { ...$scope.legendSettings, ...$scope.isEntryActive };
        if (!$scope.installation.view_permissions.update_settings) return;
        ApiService.installations($stateParams.id, 'dashboard-graph-settings', $scope.legendSettings, "PATCH");
    }

    function currentHour() {
        return moment().utcOffset($scope.installation.timezone_offset_textual).startOf('hour').unix() * 1000;
    }

    $scope.showPassthruModal = function () {
        if ($scope.ci.ss.rawValue != 8) {
            return;
        }
        VrmModalService.openConfirmModal(
            $scope,
            "passthru",
            (exit) => {
                if (exit) {
                    return;
                }
                const url = 'https://www.victronenergy.com/upload/documents/Energy_Storage_System/6292-ESS_design_and_installation_manual-pdf-en.pdf#page=31';
                window.open(url, '_blank');
            },
            {
                icon: 'sg-vrm-icon_061_info-round-16px', yesBtnClass: 'sg-site', noBtnClass: 'sg-blue', noTranslation: "common.passthru_visit_manual", yesTranslation: "common.close"
            },
            "vrm-modal--passthru"
        );
    }

    /**
     * Get time interval of the now indicator
     * @param {string} intervalStep Width of the interval. E.g. 'hours', 'days', etc.
     * @param {boolean} centerOnTick Wether to display the now indicator on the tick instead of between ticks
     * @returns {[number, number]}
     */
    function nowIndicatorInterval(intervalStep = $scope.datePicker.range.interval, centerOnTick = true) {
        let intervalUnit = intervalStep === '15mins' ? 'minutes' : intervalStep;
        let intervalDuration = intervalStep === '15mins' ? moment.duration(15, 'minutes') : moment.duration(1, intervalUnit);

        let currentTime = moment().utcOffset($scope.installation.timezone_offset_textual).startOf(intervalUnit);
        if (intervalStep === '15mins') {
            // Round down to 15 mins
            let remainder = currentTime.minute() % 15
            currentTime = currentTime.subtract(remainder, 'minutes')
        }
        currentTime = currentTime.unix() * 1000;

        let from = currentTime;
        // Center the indicator on the xAxis tick
        if (centerOnTick) {
            from -= 0.5 * intervalDuration.asMilliseconds()
        }

        let to = from + intervalDuration.asMilliseconds();

        return [from, to]
    }

    $scope.openAvatarModal = function () {
        InstallationAvatarService.openAvatarModal($scope, $scope.installation);
    };

}
